function distance(a, b) {
  const dx = Number((b?.x || 0) - (a?.x || 0));
  const dy = Number((b?.y || 0) - (a?.y || 0));
  return Math.hypot(dx, dy);
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function toPointPairs(points) {
  const list = [];
  for (let i = 0; i < (Array.isArray(points) ? points.length : 0); i += 2) {
    const x = Number(points[i]);
    const y = Number(points[i + 1]);
    if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
    list.push({ x, y });
  }
  return list;
}

function getBoundingBox(pointPairs) {
  if (!pointPairs.length) return null;
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  pointPairs.forEach((point) => {
    if (point.x < minX) minX = point.x;
    if (point.y < minY) minY = point.y;
    if (point.x > maxX) maxX = point.x;
    if (point.y > maxY) maxY = point.y;
  });

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
    center: {
      x: (minX + maxX) / 2,
      y: (minY + maxY) / 2
    }
  };
}

function getPathLength(pointPairs) {
  let total = 0;
  for (let index = 1; index < pointPairs.length; index += 1) {
    total += distance(pointPairs[index - 1], pointPairs[index]);
  }
  return total;
}

function getLineDistance(point, start, end) {
  const lineLength = distance(start, end);
  if (!lineLength) return distance(point, start);
  const area =
    Math.abs(
      (end.x - start.x) * (start.y - point.y) - (start.x - point.x) * (end.y - start.y)
    );
  return area / lineLength;
}

function pointToSegmentDistance(point, start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const denominator = dx * dx + dy * dy;
  if (!denominator) return distance(point, start);
  const t = clamp(((point.x - start.x) * dx + (point.y - start.y) * dy) / denominator, 0, 1);
  return distance(point, {
    x: start.x + dx * t,
    y: start.y + dy * t
  });
}

function normalizeMarkerFamilyCount(value, fallback = 1) {
  const normalized = Math.round(Number(value));
  if (!Number.isFinite(normalized) || normalized <= 0) {
    return clamp(Math.round(Number(fallback) || 1), 1, 3);
  }
  return clamp(normalized, 1, 3);
}

function makeMarkerFamilyId(prefix, ...parts) {
  return [prefix, ...parts]
    .map((part) => String(part || "").trim().replace(/[^\w-]+/g, "_"))
    .filter(Boolean)
    .join("__");
}

const SEGMENT_STROKE_DASHES = Object.freeze({
  solid: null,
  dashed: [10, 6],
  dotted: [2.5, 7.5]
});

function normalizeSegmentStrokeStyle(value, fallback = "solid") {
  const normalized = String(value || fallback || "solid").trim().toLowerCase();
  if (normalized === "dashed" || normalized === "dotted") return normalized;
  return "solid";
}

function resolveSegmentDashPattern(strokeStyle) {
  const normalized = normalizeSegmentStrokeStyle(strokeStyle, "solid");
  const dash = SEGMENT_STROKE_DASHES[normalized];
  return Array.isArray(dash) ? [...dash] : undefined;
}

function classifyStyledSegment(
  points,
  bounds,
  directLength,
  pathLength,
  maxDistance,
  start,
  end,
  strokeId,
  pointerType = "",
  strokeColor = "#0f172a"
) {
  const normalizedPointerType = String(pointerType || "").trim().toLowerCase();
  const isMouseStroke = normalizedPointerType === "mouse";
  if (!bounds || points.length < (isMouseStroke ? 4 : 6) || directLength < 34) return null;
  const major = Math.max(Number(bounds.width || 0), Number(bounds.height || 0));
  const minor = Math.min(Number(bounds.width || 0), Number(bounds.height || 0));
  if (major < 34 || minor > Math.max(20, major * 0.35)) return null;

  const straightness = directLength > 0 ? pathLength / directLength : Number.POSITIVE_INFINITY;
  if (!Number.isFinite(straightness) || straightness < (isMouseStroke ? 1.08 : 1.14) || straightness > 2.8) return null;
  if (maxDistance > Math.max(18, directLength * 0.18)) return null;

  const direction = normalizeVector({
    x: end.x - start.x,
    y: end.y - start.y
  });
  if (!direction) return null;
  const perpendicular = {
    x: -direction.y,
    y: direction.x
  };
  const alongMargin = directLength * 0.06;
  const deviationThreshold = clamp(directLength * (isMouseStroke ? 0.013 : 0.018), isMouseStroke ? 1.5 : 2.2, isMouseStroke ? 5.5 : 7);
  let previousSign = 0;
  let signChanges = 0;
  let significantSamples = 0;
  let maxDeviation = 0;

  points.slice(1, -1).forEach((point) => {
    const relativeX = Number(point.x || 0) - Number(start.x || 0);
    const relativeY = Number(point.y || 0) - Number(start.y || 0);
    const along = relativeX * direction.x + relativeY * direction.y;
    if (along <= alongMargin || along >= directLength - alongMargin) return;
    const deviation = relativeX * perpendicular.x + relativeY * perpendicular.y;
    const absDeviation = Math.abs(deviation);
    if (absDeviation < deviationThreshold) return;
    significantSamples += 1;
    maxDeviation = Math.max(maxDeviation, absDeviation);
    const sign = deviation >= 0 ? 1 : -1;
    if (previousSign && sign !== previousSign) signChanges += 1;
    previousSign = sign;
  });

  if (
    significantSamples < (isMouseStroke ? 2 : 4) ||
    signChanges < (isMouseStroke ? 1 : 2) ||
    maxDeviation < deviationThreshold * (isMouseStroke ? 1 : 1.2)
  ) {
    return null;
  }

  const strokeStyle =
    signChanges >= (isMouseStroke ? 3 : 4) || significantSamples >= (isMouseStroke ? 6 : 8) ? "dotted" : "dashed";
  return {
    id: strokeId,
    kind: "segment",
    strokeStyle,
    color: String(strokeColor || "#0f172a"),
    start,
    end,
    directLength,
    bounds,
    confidence: clamp(
      0.5 + signChanges * 0.06 + significantSamples * 0.015 - Math.abs(straightness - 1.42) * 0.18,
      0.42,
      0.92
    )
  };
}

function clusterNumericValues(values, threshold = 4.2) {
  const sorted = [...(Array.isArray(values) ? values : [])]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value))
    .sort((left, right) => left - right);
  if (!sorted.length) return [];

  const clusters = [];
  sorted.forEach((value) => {
    const cluster = clusters[clusters.length - 1];
    if (cluster && Math.abs(value - cluster.mean) <= threshold) {
      cluster.values.push(value);
      cluster.mean = cluster.values.reduce((sum, entry) => sum + entry, 0) / cluster.values.length;
      return;
    }
    clusters.push({
      values: [value],
      mean: value
    });
  });

  return clusters.map((cluster) => Number(cluster.mean.toFixed(2)));
}

function buildMarkerFamilyRadii(baseRadius, count) {
  const safeCount = normalizeMarkerFamilyCount(count, 1);
  const safeBaseRadius = clamp(Number(baseRadius) || 12, 8, 32);
  const spacing = clamp(safeBaseRadius * 0.18, 4.5, 6.5);
  const startOffset = (safeCount - 1) / 2;

  return Array.from({ length: safeCount }, (_, index) =>
    Number(clamp(safeBaseRadius + (index - startOffset) * spacing, 8, 32).toFixed(2))
  );
}

function applyAngleMarkOverrides(angleMarkDependencies, familyOverrides) {
  return (Array.isArray(angleMarkDependencies) ? angleMarkDependencies : []).map((dependency) => {
    const desiredCount = normalizeMarkerFamilyCount(
      familyOverrides?.[dependency.id],
      dependency.arcCount || dependency.radii?.length || 1
    );
    const rawRadii = Array.isArray(dependency.radii)
      ? dependency.radii.map((value) => Number(value)).filter((value) => Number.isFinite(value))
      : [];
    const baseRadius =
      rawRadii.length > 0
        ? rawRadii.reduce((sum, value) => sum + value, 0) / rawRadii.length
        : Number(dependency.radius) || 12;
    const radii =
      rawRadii.length === desiredCount && familyOverrides?.[dependency.id] == null
        ? rawRadii
        : buildMarkerFamilyRadii(baseRadius, desiredCount);
    return {
      ...dependency,
      arcCount: desiredCount,
      radii,
      radius: radii.reduce((sum, value) => sum + value, 0) / Math.max(radii.length, 1)
    };
  });
}

function applyMarkerCountOverrides(groups, familyOverrides) {
  return (Array.isArray(groups) ? groups : []).map((group) => ({
    ...group,
    count: normalizeMarkerFamilyCount(familyOverrides?.[group.id], group.count)
  }));
}

function getCentroid(points) {
  if (!points.length) return { x: 0, y: 0 };
  const totals = points.reduce(
    (acc, point) => {
      acc.x += point.x;
      acc.y += point.y;
      return acc;
    },
    { x: 0, y: 0 }
  );
  return {
    x: totals.x / points.length,
    y: totals.y / points.length
  };
}

function normalizeVector(vector) {
  const magnitude = Math.hypot(Number(vector?.x || 0), Number(vector?.y || 0));
  if (!magnitude) return null;
  return {
    x: Number(vector.x) / magnitude,
    y: Number(vector.y) / magnitude
  };
}

function angleBetweenVectors(vectorA, vectorB) {
  const normalizedA = normalizeVector(vectorA);
  const normalizedB = normalizeVector(vectorB);
  if (!normalizedA || !normalizedB) return 180;
  const cosine = clamp(normalizedA.x * normalizedB.x + normalizedA.y * normalizedB.y, -1, 1);
  return (Math.acos(cosine) * 180) / Math.PI;
}

function getSubpathMaxDeviation(points, startIndex, endIndex) {
  if (!Array.isArray(points) || endIndex - startIndex < 2) return 0;
  const start = points[startIndex];
  const end = points[endIndex];
  let maxDeviation = 0;
  for (let index = startIndex + 1; index < endIndex; index += 1) {
    maxDeviation = Math.max(maxDeviation, getLineDistance(points[index], start, end));
  }
  return maxDeviation;
}

function classifyRightAngleMark(points, bounds, start, end, strokeId) {
  if (!bounds || points.length < 3) return null;

  const diagonal = Math.hypot(bounds.width, bounds.height);
  if (diagonal < 9 || diagonal > 88) return null;

  const closureDistance = distance(start, end);
  if (closureDistance <= Math.max(5, diagonal * 0.32)) return null;

  let bestCorner = null;
  for (let index = 1; index < points.length - 1; index += 1) {
    const corner = points[index];
    const armALength = distance(corner, start);
    const armBLength = distance(corner, end);
    if (armALength < 6 || armBLength < 6) continue;

    const angle = angleBetweenVectors(
      { x: start.x - corner.x, y: start.y - corner.y },
      { x: end.x - corner.x, y: end.y - corner.y }
    );
    const angleDiff = Math.abs(angle - 90);
    const deviationA = getSubpathMaxDeviation(points, 0, index);
    const deviationB = getSubpathMaxDeviation(points, index, points.length - 1);
    const straightPenalty =
      deviationA / Math.max(4, armALength * 0.2) + deviationB / Math.max(4, armBLength * 0.2);
    const armBalancePenalty = Math.abs(armALength - armBLength) / Math.max(armALength, armBLength);
    const score = angleDiff + straightPenalty * 18 + armBalancePenalty * 14;

    if (!bestCorner || score < bestCorner.score) {
      bestCorner = {
        score,
        corner,
        armAEnd: start,
        armBEnd: end,
        armALength,
        armBLength,
        angleDiff,
        straightPenalty
      };
    }
  }

  if (!bestCorner || bestCorner.angleDiff > 24 || bestCorner.straightPenalty > 2.3) {
    return null;
  }

  return {
    id: strokeId,
    kind: "right_angle_mark",
    corner: bestCorner.corner,
    armAEnd: bestCorner.armAEnd,
    armBEnd: bestCorner.armBEnd,
    armAVector: normalizeVector({
      x: bestCorner.armAEnd.x - bestCorner.corner.x,
      y: bestCorner.armAEnd.y - bestCorner.corner.y
    }),
    armBVector: normalizeVector({
      x: bestCorner.armBEnd.x - bestCorner.corner.x,
      y: bestCorner.armBEnd.y - bestCorner.corner.y
    }),
    size: (bestCorner.armALength + bestCorner.armBLength) / 2,
    bounds,
    confidence: clamp(1 - bestCorner.angleDiff / 42 - bestCorner.straightPenalty * 0.18, 0.35, 0.95)
  };
}

function classifyParallelArrowMark(points, bounds, start, end, strokeId) {
  if (!bounds || points.length < 3) return null;

  const diagonal = Math.hypot(bounds.width, bounds.height);
  if (diagonal < 8 || diagonal > 72) return null;

  const closureDistance = distance(start, end);
  if (closureDistance <= Math.max(4, diagonal * 0.18)) return null;

  let bestCorner = null;
  for (let index = 1; index < points.length - 1; index += 1) {
    const corner = points[index];
    const armALength = distance(corner, start);
    const armBLength = distance(corner, end);
    if (armALength < 5 || armBLength < 5) continue;

    const angle = angleBetweenVectors(
      { x: start.x - corner.x, y: start.y - corner.y },
      { x: end.x - corner.x, y: end.y - corner.y }
    );
    if (angle < 22 || angle > 78) continue;

    const deviationA = getSubpathMaxDeviation(points, 0, index);
    const deviationB = getSubpathMaxDeviation(points, index, points.length - 1);
    const straightPenalty =
      deviationA / Math.max(3, armALength * 0.18) + deviationB / Math.max(3, armBLength * 0.18);
    const armBalancePenalty = Math.abs(armALength - armBLength) / Math.max(armALength, armBLength);
    const score = Math.abs(angle - 55) + straightPenalty * 18 + armBalancePenalty * 14;

    if (!bestCorner || score < bestCorner.score) {
      bestCorner = {
        score,
        corner,
        armAEnd: start,
        armBEnd: end,
        armALength,
        armBLength,
        angle,
        straightPenalty
      };
    }
  }

  if (!bestCorner || bestCorner.straightPenalty > 2.2) return null;

  const bisector = normalizeVector({
    x: (bestCorner.armAEnd.x + bestCorner.armBEnd.x) / 2 - bestCorner.corner.x,
    y: (bestCorner.armAEnd.y + bestCorner.armBEnd.y) / 2 - bestCorner.corner.y
  });
  if (!bisector) return null;

  return {
    id: strokeId,
    kind: "parallel_arrow_mark",
    corner: bestCorner.corner,
    armAEnd: bestCorner.armAEnd,
    armBEnd: bestCorner.armBEnd,
    bisector,
    size: (bestCorner.armALength + bestCorner.armBLength) / 2,
    bounds,
    confidence: clamp(1 - Math.abs(bestCorner.angle - 55) / 70 - bestCorner.straightPenalty * 0.16, 0.35, 0.93)
  };
}

function classifyAngleArcMark(points, bounds, pathLength, start, end, strokeId) {
  if (!bounds || points.length < 4) return null;

  const diagonal = Math.hypot(bounds.width, bounds.height);
  if (diagonal < 10 || diagonal > 120) return null;

  const closureDistance = distance(start, end);
  if (closureDistance <= Math.max(8, diagonal * 0.2)) return null;

  const directLength = distance(start, end);
  const straightness = directLength > 0 ? pathLength / directLength : Number.POSITIVE_INFINITY;
  if (straightness < 1.025 || straightness > 2.4) return null;

  const maxDeviation = points.reduce(
    (acc, point) => Math.max(acc, getLineDistance(point, start, end)),
    0
  );
  if (maxDeviation < Math.max(2.4, diagonal * 0.08)) return null;

  const midpoint = points[Math.floor(points.length / 2)];
  if (!midpoint) return null;

  return {
    id: strokeId,
    kind: "angle_arc_mark",
    points,
    start,
    end,
    midpoint,
    bounds,
    size: diagonal,
    confidence: clamp((straightness - 1) * 1.2 + maxDeviation / Math.max(18, diagonal), 0.35, 0.9)
  };
}

function classifyLengthTickMark(points, bounds, directLength, pathLength, maxDistance, start, end, strokeId) {
  if (!bounds || points.length < 2) return null;

  const diagonal = Math.hypot(bounds.width, bounds.height);
  if (directLength < 6 || directLength > 28 || diagonal > 34) return null;

  const straightness = directLength > 0 ? pathLength / directLength : Number.POSITIVE_INFINITY;
  if (straightness > 1.18) return null;
  if (maxDistance > Math.max(2.6, directLength * 0.14)) return null;

  return {
    id: strokeId,
    kind: "length_tick_mark",
    start,
    end,
    midpoint: {
      x: (start.x + end.x) / 2,
      y: (start.y + end.y) / 2
    },
    directLength,
    bounds,
    confidence: clamp(1 - maxDistance / Math.max(4, directLength * 0.18), 0.45, 0.96)
  };
}

function classifyCircle(points, bounds, pathLength, start, end, strokeId) {
  if (!bounds || points.length < 8) return null;

  const diagonal = Math.hypot(bounds.width, bounds.height);
  if (diagonal < 26 || bounds.width < 18 || bounds.height < 18) return null;

  const closureDistance = distance(start, end);
  if (closureDistance > Math.max(18, diagonal * 0.24)) return null;

  const aspectRatio = bounds.width / Math.max(bounds.height, 1);
  if (aspectRatio < 0.62 || aspectRatio > 1.45) return null;

  const center = getCentroid(points);
  const radii = points.map((point) => distance(point, center));
  const meanRadius = radii.reduce((sum, radius) => sum + radius, 0) / radii.length;
  if (meanRadius < 12) return null;

  const variance =
    radii.reduce((sum, radius) => sum + Math.pow(radius - meanRadius, 2), 0) / radii.length;
  const radiusDeviation = Math.sqrt(variance) / meanRadius;
  const expectedCircumference = 2 * Math.PI * meanRadius;
  const coverageRatio = expectedCircumference > 0 ? pathLength / expectedCircumference : 0;

  if (radiusDeviation > 0.28 || coverageRatio < 0.55 || coverageRatio > 1.7) {
    return null;
  }

  return {
    id: strokeId,
    kind: "circle",
    center,
    radius: meanRadius,
    bounds,
    confidence: clamp(1 - radiusDeviation * 1.6, 0.35, 0.96)
  };
}

function fitCircleFromThreePoints(pointA, pointB, pointC) {
  if (!pointA || !pointB || !pointC) return null;
  const x1 = Number(pointA.x);
  const y1 = Number(pointA.y);
  const x2 = Number(pointB.x);
  const y2 = Number(pointB.y);
  const x3 = Number(pointC.x);
  const y3 = Number(pointC.y);
  const determinant = 2 * (x1 * (y2 - y3) + x2 * (y3 - y1) + x3 * (y1 - y2));
  if (!Number.isFinite(determinant) || Math.abs(determinant) < 1e-6) return null;

  const ux =
    ((x1 * x1 + y1 * y1) * (y2 - y3) +
      (x2 * x2 + y2 * y2) * (y3 - y1) +
      (x3 * x3 + y3 * y3) * (y1 - y2)) /
    determinant;
  const uy =
    ((x1 * x1 + y1 * y1) * (x3 - x2) +
      (x2 * x2 + y2 * y2) * (x1 - x3) +
      (x3 * x3 + y3 * y3) * (x2 - x1)) /
    determinant;
  if (!Number.isFinite(ux) || !Number.isFinite(uy)) return null;

  const center = { x: ux, y: uy };
  const radius = distance(center, pointA);
  if (!Number.isFinite(radius) || radius <= 0) return null;

  return {
    center,
    radius
  };
}

function normalizePositiveRadians(value) {
  let angle = Number(value) || 0;
  const fullTurn = Math.PI * 2;
  while (angle < 0) angle += fullTurn;
  while (angle >= fullTurn) angle -= fullTurn;
  return angle;
}

function positiveAngleDelta(fromAngle, toAngle) {
  return normalizePositiveRadians(toAngle - fromAngle);
}

function sweepContainsAngle(startAngle, sweepAngle, testAngle, tolerance = 0.12) {
  const start = normalizePositiveRadians(startAngle);
  const test = normalizePositiveRadians(testAngle);
  if (sweepAngle >= 0) {
    const total = positiveAngleDelta(start, start + sweepAngle);
    const delta = positiveAngleDelta(start, test);
    return delta <= total + tolerance;
  }
  const total = positiveAngleDelta(start + sweepAngle, start);
  const delta = positiveAngleDelta(test, start);
  return delta <= total + tolerance;
}

function resolveArcSweep(center, startPoint, throughPoint, endPoint) {
  if (!center || !startPoint || !throughPoint || !endPoint) return null;
  const startAngle = vectorAngle({
    x: startPoint.x - center.x,
    y: startPoint.y - center.y
  });
  const midAngle = vectorAngle({
    x: throughPoint.x - center.x,
    y: throughPoint.y - center.y
  });
  const endAngle = vectorAngle({
    x: endPoint.x - center.x,
    y: endPoint.y - center.y
  });

  const ccwSweep = positiveAngleDelta(startAngle, endAngle);
  const cwSweep = ccwSweep - Math.PI * 2;
  const useCcw = sweepContainsAngle(startAngle, ccwSweep, midAngle);
  const sweepAngle = useCcw ? ccwSweep : cwSweep;
  return {
    startAngle,
    endAngle,
    midAngle,
    sweepAngle,
    sweepDegrees: Math.abs((sweepAngle * 180) / Math.PI)
  };
}

function classifyCircularArc(points, bounds, pathLength, start, end, strokeId) {
  if (!bounds || points.length < 5) return null;

  const diagonal = Math.hypot(bounds.width, bounds.height);
  if (diagonal < 18) return null;

  const closureDistance = distance(start, end);
  if (closureDistance <= Math.max(10, diagonal * 0.16)) return null;

  const midpoint = points[Math.floor(points.length / 2)];
  if (!midpoint) return null;

  const fit = fitCircleFromThreePoints(start, midpoint, end);
  if (!fit) return null;
  if (fit.radius < 10) return null;

  const radii = points.map((point) => distance(point, fit.center));
  const meanRadius = radii.reduce((sum, radius) => sum + radius, 0) / Math.max(radii.length, 1);
  if (!Number.isFinite(meanRadius) || meanRadius < 10) return null;

  const variance =
    radii.reduce((sum, radius) => sum + Math.pow(radius - meanRadius, 2), 0) / Math.max(radii.length, 1);
  const radiusDeviation = Math.sqrt(variance) / meanRadius;
  if (radiusDeviation > 0.22) return null;

  const sweep = resolveArcSweep(fit.center, start, midpoint, end);
  if (!sweep) return null;
  if (sweep.sweepDegrees < 20 || sweep.sweepDegrees > 340) return null;

  const expectedArcLength = Math.abs(sweep.sweepAngle) * meanRadius;
  const pathRatio = expectedArcLength > 0 ? pathLength / expectedArcLength : 0;
  if (pathRatio < 0.62 || pathRatio > 1.55) return null;

  return {
    id: strokeId,
    kind: "circular_arc",
    start,
    end,
    midpoint,
    center: fit.center,
    radius: meanRadius,
    startAngle: sweep.startAngle,
    endAngle: sweep.endAngle,
    sweepAngle: sweep.sweepAngle,
    sweepDegrees: sweep.sweepDegrees,
    bounds,
    confidence: clamp(1 - radiusDeviation * 1.8 - Math.abs(1 - pathRatio) * 0.4, 0.35, 0.95)
  };
}

export function classifyStroke(stroke, strokeIndex) {
  const points = toPointPairs(stroke?.points || []);
  const strokeId = `stroke_${strokeIndex + 1}`;
  if (points.length < 2) return null;

  const start = points[0];
  const end = points[points.length - 1];
  const directLength = distance(start, end);
  const pathLength = getPathLength(points);
  const bounds = getBoundingBox(points);
  const maxDistance = points.reduce(
    (acc, point) => Math.max(acc, getLineDistance(point, start, end)),
    0
  );
  const straightness = directLength > 0 ? pathLength / directLength : Number.POSITIVE_INFINITY;
  const diagonal = bounds ? Math.hypot(bounds.width, bounds.height) : 0;

  const isSegment =
    directLength >= 28 &&
    maxDistance <= Math.max(6, directLength * 0.08) &&
    straightness <= 1.12;
  if (isSegment) {
    return {
      id: strokeId,
      kind: "segment",
      strokeStyle: normalizeSegmentStrokeStyle(stroke?.strokeStyle, "solid"),
      color: String(stroke?.color || "#0f172a"),
      start,
      end,
      directLength,
      bounds,
      confidence: clamp(1 - maxDistance / Math.max(12, directLength * 0.12), 0.2, 0.99)
    };
  }

  const styledSegment = classifyStyledSegment(
    points,
    bounds,
    directLength,
    pathLength,
    maxDistance,
      start,
      end,
      strokeId,
      stroke?.pointerType,
      stroke?.color
    );
  if (styledSegment) {
    return styledSegment;
  }

  const circle = classifyCircle(points, bounds, pathLength, start, end, strokeId);
  if (circle) return circle;

  const parallelArrowMark = classifyParallelArrowMark(points, bounds, start, end, strokeId);
  if (parallelArrowMark) return parallelArrowMark;

  const rightAngleMark = classifyRightAngleMark(points, bounds, start, end, strokeId);
  if (rightAngleMark) return rightAngleMark;

  const circularArc = classifyCircularArc(points, bounds, pathLength, start, end, strokeId);
  const angleArcMark = classifyAngleArcMark(points, bounds, pathLength, start, end, strokeId);
  if (
    circularArc &&
    (!angleArcMark || circularArc.sweepDegrees >= 85 || circularArc.radius >= 18 || diagonal >= 42)
  ) {
    return circularArc;
  }
  if (angleArcMark) return angleArcMark;
  if (circularArc) return circularArc;

  const lengthTickMark = classifyLengthTickMark(
    points,
    bounds,
    directLength,
    pathLength,
    maxDistance,
    start,
    end,
    strokeId
  );
  if (lengthTickMark) return lengthTickMark;

  return {
    id: strokeId,
    kind: "mark",
    points,
    start,
    end,
    midpoint: points[Math.floor(points.length / 2)] || start,
    bounds,
    diagonal,
    confidence: clamp(diagonal <= 42 ? 0.85 : 0.5, 0.2, 0.9)
  };
}

function mergeNode(nodes, point, snapDistance = 18, metadata = {}) {
  const source = String(metadata?.source || "stroke").trim().toLowerCase();
  const prefersExactPlacement = source === "computed" || metadata.role === "circle_center";
  const nextSplitOrder = Number.isFinite(Number(metadata?.splitOrder)) ? Number(metadata.splitOrder) : null;

  for (const node of nodes) {
    if (distance(node, point) <= snapDistance) {
      node.samples.push(point);
      if (prefersExactPlacement) {
        node.x = point.x;
        node.y = point.y;
        node.locked = true;
      } else if (!node.locked && node.samples.length <= 2) {
        node.x = node.samples.reduce((sum, item) => sum + item.x, 0) / node.samples.length;
        node.y = node.samples.reduce((sum, item) => sum + item.y, 0) / node.samples.length;
      }
      if (metadata.role && !node.role) {
        node.role = metadata.role;
      }
      if (Number.isFinite(nextSplitOrder)) {
        const existingSplitOrder = Number.isFinite(Number(node.splitOrder)) ? Number(node.splitOrder) : Infinity;
        node.splitOrder = Math.min(existingSplitOrder, nextSplitOrder);
      }
      return node;
    }
  }

  const nextNode = {
    id: `node_${nodes.length + 1}`,
    x: point.x,
    y: point.y,
    samples: [point],
    role: metadata.role || null,
    locked: prefersExactPlacement,
    splitOrder: Number.isFinite(nextSplitOrder) ? nextSplitOrder : null
  };
  nodes.push(nextNode);
  return nextNode;
}

function intersectSegments(segmentA, segmentB) {
  const x1 = segmentA.start.x;
  const y1 = segmentA.start.y;
  const x2 = segmentA.end.x;
  const y2 = segmentA.end.y;
  const x3 = segmentB.start.x;
  const y3 = segmentB.start.y;
  const x4 = segmentB.end.x;
  const y4 = segmentB.end.y;

  const denominator = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denominator) < 1e-6) return null;

  const px =
    ((x1 * y2 - y1 * x2) * (x3 - x4) - (x1 - x2) * (x3 * y4 - y3 * x4)) / denominator;
  const py =
    ((x1 * y2 - y1 * x2) * (y3 - y4) - (y1 - y2) * (x3 * y4 - y3 * x4)) / denominator;

  const within = (value, a, b) => value >= Math.min(a, b) - 1e-6 && value <= Math.max(a, b) + 1e-6;
  if (
    !within(px, x1, x2) ||
    !within(py, y1, y2) ||
    !within(px, x3, x4) ||
    !within(py, y3, y4)
  ) {
    return null;
  }

  return { x: px, y: py };
}

function getSegmentDirectionFromNode(segment, node) {
  if (!segment || !node) return null;
  if (segment.from.id === node.id) {
    return normalizeVector({
      x: segment.to.x - node.x,
      y: segment.to.y - node.y
    });
  }
  if (segment.to.id === node.id) {
    return normalizeVector({
      x: segment.from.x - node.x,
      y: segment.from.y - node.y
    });
  }
  return null;
}

function matchDirectionPair(directionA, directionB, armA, armB) {
  const optionOne = {
    total:
      angleBetweenVectors(directionA, armA) + angleBetweenVectors(directionB, armB),
    max: Math.max(angleBetweenVectors(directionA, armA), angleBetweenVectors(directionB, armB))
  };
  const optionTwo = {
    total:
      angleBetweenVectors(directionA, armB) + angleBetweenVectors(directionB, armA),
    max: Math.max(angleBetweenVectors(directionA, armB), angleBetweenVectors(directionB, armA))
  };
  return optionOne.total <= optionTwo.total ? optionOne : optionTwo;
}

function matchRightAngleMarkToSegments(mark, segments, nodes = []) {
  if (!mark || !Array.isArray(segments) || segments.length < 2) return null;

  const nodeMap = new Map();
  [...(Array.isArray(nodes) ? nodes : []), ...segments.flatMap((segment) => [segment?.from, segment?.to])]
    .filter((node) => node?.id)
    .forEach((node) => nodeMap.set(node.id, node));

  const incidentByNode = new Map();
  nodeMap.forEach((_node, nodeId) => {
    incidentByNode.set(nodeId, []);
  });

  segments.forEach((segment) => {
    if (!segment?.from?.id || !segment?.to?.id) return;
    const refId = segment.supportId || segment.id;
    const span = distance(segment.from, segment.to);
    const maxProjectionDistance = clamp(span * 0.045, 6, 12);

    nodeMap.forEach((node) => {
      if (!node?.id) return;
      const existing = incidentByNode.get(node.id) || [];
      if (segment.from.id === node.id) {
        existing.push({
          id: `${segment.id}__ray_from_${node.id}`,
          refId,
          from: node,
          to: segment.to,
          start: { x: node.x, y: node.y },
          end: { x: segment.to.x, y: segment.to.y }
        });
        incidentByNode.set(node.id, existing);
        return;
      }
      if (segment.to.id === node.id) {
        existing.push({
          id: `${segment.id}__ray_to_${node.id}`,
          refId,
          from: node,
          to: segment.from,
          start: { x: node.x, y: node.y },
          end: { x: segment.from.x, y: segment.from.y }
        });
        incidentByNode.set(node.id, existing);
        return;
      }

      const projection = projectPointOntoSegment(node, segment.from, segment.to);
      if (projection.distance > maxProjectionDistance) return;
      if (projection.rawT <= 0.08 || projection.rawT >= 0.92) return;

      existing.push(
        {
          id: `${segment.id}__interior_from_${node.id}`,
          refId,
          from: node,
          to: segment.from,
          start: { x: node.x, y: node.y },
          end: { x: segment.from.x, y: segment.from.y }
        },
        {
          id: `${segment.id}__interior_to_${node.id}`,
          refId,
          from: node,
          to: segment.to,
          start: { x: node.x, y: node.y },
          end: { x: segment.to.x, y: segment.to.y }
        }
      );
      incidentByNode.set(node.id, existing);
    });
  });

  let best = null;
  incidentByNode.forEach((incidentSegments, nodeId) => {
    if ((incidentSegments || []).length < 2) return;
    const node = nodeMap.get(nodeId);
    if (!node) return;

    const cornerDistance = distance(node, mark.corner);
    if (cornerDistance > Math.max(54, mark.size * 3.8)) return;

    for (let index = 0; index < incidentSegments.length; index += 1) {
      for (let otherIndex = index + 1; otherIndex < incidentSegments.length; otherIndex += 1) {
        const segmentA = incidentSegments[index];
        const segmentB = incidentSegments[otherIndex];
        if ((segmentA?.refId || segmentA?.id) === (segmentB?.refId || segmentB?.id)) continue;
        const directionA = getSegmentDirectionFromNode(segmentA, node);
        const directionB = getSegmentDirectionFromNode(segmentB, node);
        if (!directionA || !directionB) continue;

        const segmentAngle = angleBetweenVectors(directionA, directionB);
        if (Math.abs(segmentAngle - 90) > 48) continue;

        const directionMatch = matchDirectionPair(
          directionA,
          directionB,
          mark.armAVector,
          mark.armBVector
        );
        const endpointOptionOne = {
          total:
            pointToSegmentDistance(mark.armAEnd, segmentA.start, segmentA.end) +
            pointToSegmentDistance(mark.armBEnd, segmentB.start, segmentB.end),
          max: Math.max(
            pointToSegmentDistance(mark.armAEnd, segmentA.start, segmentA.end),
            pointToSegmentDistance(mark.armBEnd, segmentB.start, segmentB.end)
          )
        };
        const endpointOptionTwo = {
          total:
            pointToSegmentDistance(mark.armAEnd, segmentB.start, segmentB.end) +
            pointToSegmentDistance(mark.armBEnd, segmentA.start, segmentA.end),
          max: Math.max(
            pointToSegmentDistance(mark.armAEnd, segmentB.start, segmentB.end),
            pointToSegmentDistance(mark.armBEnd, segmentA.start, segmentA.end)
          )
        };
        const endpointMatch =
          endpointOptionOne.total <= endpointOptionTwo.total ? endpointOptionOne : endpointOptionTwo;
        if (endpointMatch.max > 24) continue;
        if (directionMatch.max > 42 && endpointMatch.total > 18) continue;

        const toCorner = normalizeVector({
          x: mark.corner.x - node.x,
          y: mark.corner.y - node.y
        });
        const wedgePenalty = toCorner
          ? Math.max(angleBetweenVectors(toCorner, directionA), angleBetweenVectors(toCorner, directionB))
          : 180;
        if (wedgePenalty > 110) continue;

        const score =
          cornerDistance * 1.2 +
          endpointMatch.total * 2.4 +
          directionMatch.total * 0.28 +
          Math.abs(segmentAngle - 90) * 0.45 +
          wedgePenalty * 0.08;
        if (!best || score < best.score) {
          const size = clamp(Math.min(distance(node, segmentA.to), distance(node, segmentB.to), mark.size * 1.4) * 0.26, 10, 18);
          const first = {
            x: node.x + directionA.x * size,
            y: node.y + directionA.y * size
          };
          const third = {
            x: node.x + directionB.x * size,
            y: node.y + directionB.y * size
          };
          const corner = {
            x: first.x + directionB.x * size,
            y: first.y + directionB.y * size
          };
          best = {
            type: "perpendicular",
            pointId: node.id,
            refs: [segmentA.refId || segmentA.id, segmentB.refId || segmentB.id],
            score,
            confidence: clamp(
              Number(mark.confidence || 0.5) -
                endpointMatch.total / 44 -
                directionMatch.total / 180 -
                cornerDistance / 180,
              0.35,
              0.94
            ),
            sourceMarkId: mark.id,
            rightAnglePoints: [first, corner, third]
          };
        }
      }
    }
  });

  return best;
}

function vectorAngle(vector) {
  return Math.atan2(Number(vector?.y || 0), Number(vector?.x || 0));
}

function normalizeRadians(value) {
  let angle = Number(value) || 0;
  while (angle <= -Math.PI) angle += Math.PI * 2;
  while (angle > Math.PI) angle -= Math.PI * 2;
  return angle;
}

function shortestSignedAngle(fromAngle, toAngle) {
  return normalizeRadians(toAngle - fromAngle);
}

function isAngleWithinMinorArc(testAngle, startAngle, endAngle, tolerance = 0.28) {
  const total = Math.abs(shortestSignedAngle(startAngle, endAngle));
  const toTest = Math.abs(shortestSignedAngle(startAngle, testAngle));
  const fromTest = Math.abs(shortestSignedAngle(testAngle, endAngle));
  return toTest <= total + tolerance && fromTest <= total + tolerance && Math.abs(toTest + fromTest - total) <= tolerance * 2;
}

function getOtherEndpoint(segment, node) {
  if (!segment || !node) return null;
  if (segment.from.id === node.id) return segment.to;
  if (segment.to.id === node.id) return segment.from;
  return null;
}

function projectPointOntoSegment(point, start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const denominator = dx * dx + dy * dy;
  if (!denominator) {
    return {
      point: { x: start.x, y: start.y },
      rawT: 0,
      t: 0,
      distance: distance(point, start)
    };
  }
  const rawT = ((point.x - start.x) * dx + (point.y - start.y) * dy) / denominator;
  const t = clamp(rawT, 0, 1);
  const projected = {
    x: start.x + dx * t,
    y: start.y + dy * t
  };
  return {
    point: projected,
    rawT,
    t,
    distance: distance(point, projected)
  };
}

function intersectSegmentWithCircle(start, end, center, radius, tolerance = 1e-6) {
  const dx = Number(end?.x || 0) - Number(start?.x || 0);
  const dy = Number(end?.y || 0) - Number(start?.y || 0);
  const fx = Number(start?.x || 0) - Number(center?.x || 0);
  const fy = Number(start?.y || 0) - Number(center?.y || 0);
  const a = dx * dx + dy * dy;
  if (!a) return [];

  const b = 2 * (fx * dx + fy * dy);
  const c = fx * fx + fy * fy - radius * radius;
  const discriminant = b * b - 4 * a * c;
  if (discriminant < -tolerance) return [];

  const roots = [];
  if (Math.abs(discriminant) <= tolerance) {
    roots.push(-b / (2 * a));
  } else {
    const sqrtDiscriminant = Math.sqrt(Math.max(discriminant, 0));
    roots.push((-b - sqrtDiscriminant) / (2 * a));
    roots.push((-b + sqrtDiscriminant) / (2 * a));
  }

  const points = [];
  roots.forEach((tValue) => {
    if (!Number.isFinite(tValue) || tValue < -tolerance || tValue > 1 + tolerance) return;
    const t = clamp(tValue, 0, 1);
    const point = {
      x: Number(start.x) + dx * t,
      y: Number(start.y) + dy * t
    };
    if (!points.some((existing) => distance(existing, point) <= 2.5)) {
      points.push(point);
    }
  });

  return points;
}

function detectSegmentCircleTangentPoint(start, end, center, radius) {
  const projection = projectPointOntoSegment(center, start, end);
  const span = distance(start, end);
  if (!Number.isFinite(projection?.rawT) || projection.rawT <= 0.04 || projection.rawT >= 0.96) return null;

  const radialDelta = Math.abs(Number(projection.distance || 0) - Number(radius || 0));
  const tangentTolerance = clamp(Number(radius || 0) * 0.045, 2.5, 8);
  const exactIntersections = intersectSegmentWithCircle(start, end, center, radius);

  if (exactIntersections.length === 1) {
    return exactIntersections[0];
  }

  if (exactIntersections.length >= 2) {
    const chordLength = distance(exactIntersections[0], exactIntersections[1]);
    const tangentChordTolerance = clamp(Number(radius || 0) * 0.16, 4, 12);
    if (chordLength <= tangentChordTolerance && radialDelta <= tangentTolerance * 1.6) {
      return {
        x: (exactIntersections[0].x + exactIntersections[1].x) / 2,
        y: (exactIntersections[0].y + exactIntersections[1].y) / 2
      };
    }
    return null;
  }

  if (span < 12 || radialDelta > tangentTolerance) return null;
  return projection.point;
}

function buildCurveIntersectionPoints(rawSegments, arcs, circles) {
  const supportSegments = Array.isArray(rawSegments) ? rawSegments : [];
  const arcPrimitives = Array.isArray(arcs) ? arcs : [];
  const circlePrimitives = Array.isArray(circles) ? circles : [];
  const points = [];

  function pushPoint(point, splitOrder = null) {
    if (!point) return;
    const existing = points.find((entry) => distance(entry.point, point) <= 3);
    if (existing) {
      if (Number.isFinite(Number(splitOrder))) {
        const existingSplitOrder = Number.isFinite(Number(existing.splitOrder))
          ? Number(existing.splitOrder)
          : Infinity;
        existing.splitOrder = Math.min(existingSplitOrder, Number(splitOrder));
      }
      return;
    }
    points.push({
      point,
      splitOrder: Number.isFinite(Number(splitOrder)) ? Number(splitOrder) : null
    });
  }

  supportSegments.forEach((segment) => {
    circlePrimitives.forEach((circle) => {
      const tangentPoint = detectSegmentCircleTangentPoint(segment.from, segment.to, circle.center, circle.radius);
      if (tangentPoint) {
        pushPoint(tangentPoint, segment.order);
        return;
      }
      intersectSegmentWithCircle(segment.from, segment.to, circle.center, circle.radius).forEach((point) =>
        pushPoint(point, segment.order)
      );
    });
    arcPrimitives.forEach((arc) => {
      const tangentPoint = detectSegmentCircleTangentPoint(segment.from, segment.to, arc.center, arc.radius);
      if (tangentPoint) {
        const tangentAngle = vectorAngle({
          x: tangentPoint.x - arc.center.x,
          y: tangentPoint.y - arc.center.y
        });
        if (sweepContainsAngle(arc.startAngle, arc.sweepAngle, tangentAngle, 0.2)) {
          pushPoint(tangentPoint, segment.order);
          return;
        }
      }
      intersectSegmentWithCircle(segment.from, segment.to, arc.center, arc.radius).forEach((point) => {
        const angle = vectorAngle({
          x: point.x - arc.center.x,
          y: point.y - arc.center.y
        });
        if (sweepContainsAngle(arc.startAngle, arc.sweepAngle, angle, 0.2)) {
          pushPoint(point, segment.order);
        }
      });
    });
  });

  return points;
}

function matchLengthTickMarksToSegments(marks, segments) {
  const attachments = [];
  const matchedMarkIds = new Set();

  (Array.isArray(marks) ? marks : []).forEach((mark) => {
    const tickDirection = normalizeVector({
      x: mark.end.x - mark.start.x,
      y: mark.end.y - mark.start.y
    });
    if (!tickDirection) return;

    let best = null;
    (Array.isArray(segments) ? segments : []).forEach((segment) => {
      const segmentDirection = normalizeVector({
        x: segment.to.x - segment.from.x,
        y: segment.to.y - segment.from.y
      });
      if (!segmentDirection) return;

      const segmentLength = distance(segment.from, segment.to);
      if (segmentLength < 18) return;

      const angleDiff = Math.abs(angleBetweenVectors(segmentDirection, tickDirection) - 90);
      if (angleDiff > 34) return;

      const midpointProjection = projectPointOntoSegment(mark.midpoint, segment.from, segment.to);
      if (midpointProjection.rawT < -0.08 || midpointProjection.rawT > 1.08) return;
      if (midpointProjection.t < 0.16 || midpointProjection.t > 0.84) return;
      if (midpointProjection.distance > Math.max(10, mark.directLength * 0.52)) return;

      const nearestEndpointDistance = Math.min(
        distance(mark.midpoint, segment.from),
        distance(mark.midpoint, segment.to)
      );
      const endpointExclusionDistance = Math.max(
        14,
        mark.directLength * 1.28,
        Math.min(24, segmentLength * 0.16)
      );
      if (nearestEndpointDistance < endpointExclusionDistance) return;

      const endpointDistanceA = pointToSegmentDistance(mark.start, segment.from, segment.to);
      const endpointDistanceB = pointToSegmentDistance(mark.end, segment.from, segment.to);
      const endpointDistance = (endpointDistanceA + endpointDistanceB) / 2;
      if (endpointDistance > Math.max(12, mark.directLength * 0.72)) return;

      const score =
        midpointProjection.distance * 3 +
        endpointDistance * 0.8 +
        angleDiff * 0.7 +
        nearestEndpointDistance < endpointExclusionDistance + 8 ? 36 : 0 +
        Math.abs(midpointProjection.t - 0.5) * 8;

      if (!best || score < best.score) {
        best = {
          markId: mark.id,
          segmentId: segment.id,
          supportId: segment.supportId || segment.id,
          anchorPoint: midpointProjection.point,
          anchorT: midpointProjection.t,
          segmentLength,
          score,
          confidence: clamp(
            Number(mark.confidence || 0.5) -
              midpointProjection.distance / 36 -
              endpointDistance / 42 -
              angleDiff / 120,
            0.35,
            0.92
          )
        };
      }
    });

    if (best) {
      attachments.push(best);
      matchedMarkIds.add(mark.id);
    }
  });

  return { attachments, matchedMarkIds };
}

function buildLengthTickGroups(attachments) {
  const bySegment = new Map();
  (Array.isArray(attachments) ? attachments : []).forEach((item) => {
    const existing = bySegment.get(item.segmentId) || [];
    existing.push(item);
    bySegment.set(item.segmentId, existing);
  });

  const groups = [];
  bySegment.forEach((items, segmentId) => {
    const sorted = [...items].sort((a, b) => a.anchorT - b.anchorT);
    const clusters = [];

    sorted.forEach((item) => {
      const threshold = Math.max(10, Math.min(24, item.segmentLength * 0.16));
      const cluster = clusters[clusters.length - 1];
      if (cluster && Math.abs(item.anchorT - cluster.anchorT) * item.segmentLength <= threshold) {
        cluster.items.push(item);
        cluster.anchorT =
          cluster.items.reduce((sum, entry) => sum + entry.anchorT, 0) / cluster.items.length;
        cluster.anchorPoint = {
          x: cluster.items.reduce((sum, entry) => sum + entry.anchorPoint.x, 0) / cluster.items.length,
          y: cluster.items.reduce((sum, entry) => sum + entry.anchorPoint.y, 0) / cluster.items.length
        };
        return;
      }

      clusters.push({
        items: [item],
        anchorT: item.anchorT,
        anchorPoint: item.anchorPoint,
        segmentLength: item.segmentLength
      });
    });

    const bestCluster = [...clusters].sort((left, right) => {
      if (right.items.length !== left.items.length) return right.items.length - left.items.length;
      const leftConfidence =
        left.items.reduce((sum, item) => sum + Number(item.confidence || 0), 0) / left.items.length;
      const rightConfidence =
        right.items.reduce((sum, item) => sum + Number(item.confidence || 0), 0) / right.items.length;
      return rightConfidence - leftConfidence;
    })[0];
    if (!bestCluster) return;

    groups.push({
      id: makeMarkerFamilyId("length_tick_group", segmentId),
      segmentId,
      count: normalizeMarkerFamilyCount(bestCluster.items.length, 1),
      anchorPoint: bestCluster.anchorPoint,
      anchorT: bestCluster.anchorT,
      segmentLength: bestCluster.segmentLength,
      markIds: bestCluster.items.map((item) => item.markId),
      supportId: bestCluster.items[0]?.supportId || segmentId,
      confidence:
        bestCluster.items.reduce((sum, item) => sum + Number(item.confidence || 0), 0) /
        bestCluster.items.length
    });
  });

  return groups;
}

function buildEqualLengthDependencies(lengthTickGroups) {
  const byCount = new Map();
  (Array.isArray(lengthTickGroups) ? lengthTickGroups : []).forEach((group) => {
    if (!Number.isFinite(group?.count) || group.count <= 0) return;
    const existing = byCount.get(group.count) || [];
    existing.push(group);
    byCount.set(group.count, existing);
  });

  const dependencies = [];
  byCount.forEach((groups, tickCount) => {
    const refs = [...new Set(groups.map((group) => group.segmentId))];
    if (refs.length < 2) return;
    dependencies.push({
      type: "equal_length",
      refs,
      tickCount,
      confidence:
        groups.reduce((sum, group) => sum + Number(group.confidence || 0), 0) / Math.max(groups.length, 1),
      markGroupIds: groups.map((group) => group.id)
    });
  });

  return dependencies;
}

function matchParallelArrowMarksToSegments(marks, segments) {
  const attachments = [];
  const matchedMarkIds = new Set();

  (Array.isArray(marks) ? marks : []).forEach((mark) => {
    let best = null;
    (Array.isArray(segments) ? segments : []).forEach((segment) => {
      const segmentDirection = normalizeVector({
        x: segment.to.x - segment.from.x,
        y: segment.to.y - segment.from.y
      });
      if (!segmentDirection) return;

      const segmentLength = distance(segment.from, segment.to);
      if (segmentLength < 18) return;

      const projection = projectPointOntoSegment(mark.corner, segment.from, segment.to);
      if (projection.rawT < -0.06 || projection.rawT > 1.06) return;
      if (projection.t < 0.08 || projection.t > 0.92) return;
      if (projection.distance > Math.max(9, mark.size * 0.46)) return;

      const axisAngle = angleBetweenVectors(segmentDirection, mark.bisector);
      const parallelDiff = Math.min(axisAngle, Math.abs(180 - axisAngle));
      if (parallelDiff > 28) return;

      const endpointDistance =
        (pointToSegmentDistance(mark.armAEnd, segment.from, segment.to) +
          pointToSegmentDistance(mark.armBEnd, segment.from, segment.to)) /
        2;
      if (endpointDistance < Math.max(3, mark.size * 0.18) || endpointDistance > Math.max(18, mark.size * 1.15)) {
        return;
      }

      const orientationSign =
        segmentDirection.x * mark.bisector.x + segmentDirection.y * mark.bisector.y >= 0 ? 1 : -1;
      const score =
        projection.distance * 3.2 +
        parallelDiff * 0.9 +
        endpointDistance * 0.45 +
        Math.abs(projection.t - 0.5) * 9;

      if (!best || score < best.score) {
        best = {
          markId: mark.id,
          segmentId: segment.id,
          supportId: segment.supportId || segment.id,
          anchorPoint: projection.point,
          anchorT: projection.t,
          segmentLength,
          orientationSign,
          score,
          confidence: clamp(
            Number(mark.confidence || 0.5) -
              projection.distance / 28 -
              parallelDiff / 90 -
              endpointDistance / 80,
            0.35,
            0.92
          )
        };
      }
    });

    if (best) {
      attachments.push(best);
      matchedMarkIds.add(mark.id);
    }
  });

  return { attachments, matchedMarkIds };
}

function buildParallelArrowGroups(attachments) {
  const bySegment = new Map();
  (Array.isArray(attachments) ? attachments : []).forEach((item) => {
    const existing = bySegment.get(item.segmentId) || [];
    existing.push(item);
    bySegment.set(item.segmentId, existing);
  });

  const groups = [];
  bySegment.forEach((items, segmentId) => {
    const sorted = [...items].sort((a, b) => a.anchorT - b.anchorT);
    const clusters = [];

    sorted.forEach((item) => {
      const threshold = Math.max(12, Math.min(26, item.segmentLength * 0.18));
      const cluster = clusters[clusters.length - 1];
      if (cluster && Math.abs(item.anchorT - cluster.anchorT) * item.segmentLength <= threshold) {
        cluster.items.push(item);
        cluster.anchorT =
          cluster.items.reduce((sum, entry) => sum + entry.anchorT, 0) / cluster.items.length;
        cluster.anchorPoint = {
          x: cluster.items.reduce((sum, entry) => sum + entry.anchorPoint.x, 0) / cluster.items.length,
          y: cluster.items.reduce((sum, entry) => sum + entry.anchorPoint.y, 0) / cluster.items.length
        };
        cluster.orientationSign =
          cluster.items.reduce((sum, entry) => sum + Number(entry.orientationSign || 0), 0) >= 0 ? 1 : -1;
        return;
      }

      clusters.push({
        items: [item],
        anchorT: item.anchorT,
        anchorPoint: item.anchorPoint,
        segmentLength: item.segmentLength,
        orientationSign: item.orientationSign
      });
    });

    const bestCluster = [...clusters].sort((left, right) => {
      if (right.items.length !== left.items.length) return right.items.length - left.items.length;
      const leftConfidence =
        left.items.reduce((sum, item) => sum + Number(item.confidence || 0), 0) / left.items.length;
      const rightConfidence =
        right.items.reduce((sum, item) => sum + Number(item.confidence || 0), 0) / right.items.length;
      return rightConfidence - leftConfidence;
    })[0];
    if (!bestCluster) return;

    groups.push({
      id: makeMarkerFamilyId("parallel_arrow_group", bestCluster.items[0]?.supportId || segmentId),
      segmentId,
      count: normalizeMarkerFamilyCount(bestCluster.items.length, 1),
      anchorPoint: bestCluster.anchorPoint,
      anchorT: bestCluster.anchorT,
      segmentLength: bestCluster.segmentLength,
      orientationSign: bestCluster.orientationSign,
      markIds: bestCluster.items.map((item) => item.markId),
      supportId: bestCluster.items[0]?.supportId || segmentId,
      confidence:
        bestCluster.items.reduce((sum, item) => sum + Number(item.confidence || 0), 0) /
        bestCluster.items.length
    });
  });

  return groups;
}

function buildParallelDependencies(parallelArrowGroups) {
  const byCount = new Map();
  (Array.isArray(parallelArrowGroups) ? parallelArrowGroups : []).forEach((group) => {
    if (!Number.isFinite(group?.count) || group.count <= 0) return;
    const existing = byCount.get(group.count) || [];
    existing.push(group);
    byCount.set(group.count, existing);
  });

  const dependencies = [];
  byCount.forEach((groups, arrowCount) => {
    const refs = [...new Set(groups.map((group) => group.supportId || group.segmentId))];
    if (refs.length < 2) return;
    dependencies.push({
      type: "parallel",
      refs,
      arrowCount,
      confidence:
        groups.reduce((sum, group) => sum + Number(group.confidence || 0), 0) / Math.max(groups.length, 1),
      markGroupIds: groups.map((group) => group.id)
    });
  });

  return dependencies;
}

function matchAngleArcMarkToSegments(mark, segments) {
  if (!mark || !Array.isArray(segments) || segments.length < 2) return null;

  const nodeMap = new Map();
  const incidentByNode = new Map();
  segments.forEach((segment) => {
    [segment.from, segment.to].forEach((node) => {
      nodeMap.set(node.id, node);
      const existing = incidentByNode.get(node.id) || [];
      existing.push(segment);
      incidentByNode.set(node.id, existing);
    });
  });

  let best = null;
  incidentByNode.forEach((incidentSegments, nodeId) => {
    if ((incidentSegments || []).length < 2) return;
    const node = nodeMap.get(nodeId);
    if (!node) return;

    const radii = mark.points.map((point) => distance(point, node));
    const meanRadius = radii.reduce((sum, value) => sum + value, 0) / Math.max(radii.length, 1);
    if (!Number.isFinite(meanRadius) || meanRadius < 6 || meanRadius > 72) return;
    const radiusVariance =
      radii.reduce((sum, value) => sum + Math.pow(value - meanRadius, 2), 0) / Math.max(radii.length, 1);
    const radiusDeviation = Math.sqrt(radiusVariance) / Math.max(meanRadius, 1);
    if (radiusDeviation > 0.24) return;

    const startVector = normalizeVector({
      x: mark.start.x - node.x,
      y: mark.start.y - node.y
    });
    const endVector = normalizeVector({
      x: mark.end.x - node.x,
      y: mark.end.y - node.y
    });
    const midVector = normalizeVector({
      x: mark.midpoint.x - node.x,
      y: mark.midpoint.y - node.y
    });
    if (!startVector || !endVector || !midVector) return;

    for (let index = 0; index < incidentSegments.length; index += 1) {
      for (let otherIndex = index + 1; otherIndex < incidentSegments.length; otherIndex += 1) {
        const segmentA = incidentSegments[index];
        const segmentB = incidentSegments[otherIndex];
        const directionA = getSegmentDirectionFromNode(segmentA, node);
        const directionB = getSegmentDirectionFromNode(segmentB, node);
        if (!directionA || !directionB) continue;

        const segmentAngle = angleBetweenVectors(directionA, directionB);
        if (segmentAngle < 18 || segmentAngle > 170) continue;

        const directionMatch = matchDirectionPair(
          directionA,
          directionB,
          startVector,
          endVector
        );
        const endpointOptionOne = {
          total:
            pointToSegmentDistance(mark.start, segmentA.start, segmentA.end) +
            pointToSegmentDistance(mark.end, segmentB.start, segmentB.end),
          max: Math.max(
            pointToSegmentDistance(mark.start, segmentA.start, segmentA.end),
            pointToSegmentDistance(mark.end, segmentB.start, segmentB.end)
          )
        };
        const endpointOptionTwo = {
          total:
            pointToSegmentDistance(mark.start, segmentB.start, segmentB.end) +
            pointToSegmentDistance(mark.end, segmentA.start, segmentA.end),
          max: Math.max(
            pointToSegmentDistance(mark.start, segmentB.start, segmentB.end),
            pointToSegmentDistance(mark.end, segmentA.start, segmentA.end)
          )
        };
        const endpointMatch =
          endpointOptionOne.total <= endpointOptionTwo.total ? endpointOptionOne : endpointOptionTwo;
        if (endpointMatch.max > 22) continue;
        if (directionMatch.max > 48 && endpointMatch.total > 20) continue;

        const angleA = vectorAngle(directionA);
        const angleB = vectorAngle(directionB);
        const midAngle = vectorAngle(midVector);
        if (!isAngleWithinMinorArc(midAngle, angleA, angleB)) continue;

        const score =
          endpointMatch.total * 2.2 +
          directionMatch.total * 0.35 +
          radiusDeviation * 140 +
          Math.abs(segmentAngle - angleBetweenVectors(startVector, endVector)) * 0.18;
        if (!best || score < best.score) {
          best = {
            type: "angle_mark",
            pointId: node.id,
            refs: [segmentA.id, segmentB.id],
            anglePointIds: [
              getOtherEndpoint(segmentA, node)?.id,
              node.id,
              getOtherEndpoint(segmentB, node)?.id
            ].filter(Boolean),
            radius: meanRadius,
            score,
            confidence: clamp(
              Number(mark.confidence || 0.5) -
                endpointMatch.total / 60 -
                radiusDeviation * 0.6 -
                directionMatch.total / 220,
              0.35,
              0.92
            ),
            sourceMarkId: mark.id
          };
        }
      }
    }
  });

  return best;
}

function normalizePosition(point, stageSize) {
  const width = Math.max(1, Number(stageSize?.width) || 1);
  const height = Math.max(1, Number(stageSize?.height) || 1);
  return [
    Number(((point.x / width) * 100).toFixed(2)),
    Number(((point.y / height) * 100).toFixed(2))
  ];
}

function normalizeRadius(radius, stageSize) {
  const basis = Math.max(1, Math.min(Number(stageSize?.width) || 1, Number(stageSize?.height) || 1));
  return Number(((Number(radius) / basis) * 100).toFixed(2));
}

function normalizeSuggestedPosition(pos) {
  if (Array.isArray(pos) && pos.length >= 2) {
    const x = Number(pos[0]);
    const y = Number(pos[1]);
    if (Number.isFinite(x) && Number.isFinite(y)) {
      return {
        x: Number((Math.abs(x) <= 1.2 ? x * 100 : x).toFixed(2)),
        y: Number((Math.abs(y) <= 1.2 ? y * 100 : y).toFixed(2))
      };
    }
  }

  if (pos && typeof pos === "object") {
    const x = Number(pos.x);
    const y = Number(pos.y);
    if (Number.isFinite(x) && Number.isFinite(y)) {
      return {
        x: Number((Math.abs(x) <= 1.2 ? x * 100 : x).toFixed(2)),
        y: Number((Math.abs(y) <= 1.2 ? y * 100 : y).toFixed(2))
      };
    }
  }

  return null;
}

function buildFallbackLabel(index) {
  const alphabetIndex = Number(index) % 26;
  const cycle = Math.floor(Number(index) / 26);
  const letter = String.fromCharCode(65 + alphabetIndex);
  return cycle > 0 ? `${letter}${cycle}` : letter;
}

function buildCenterFallbackLabel(index) {
  return index <= 0 ? "O" : `O${index}`;
}

function uniqueLabels(values) {
  const seen = new Set();
  return (Array.isArray(values) ? values : []).reduce((acc, item) => {
    const label = String(item || "").trim();
    if (!label) return acc;
    const key = label.toLowerCase();
    if (seen.has(key)) return acc;
    seen.add(key);
    acc.push(label);
    return acc;
  }, []);
}

function unwrapTemplateToken(value) {
  return String(value || "")
    .trim()
    .replace(/^\{\{\s*/, "")
    .replace(/\s*\}\}$/, "");
}

function buildTemplateLabel(rawValue, kind = "constant") {
  const cleaned = String(rawValue || "").trim();
  if (!cleaned) {
    return {
      text: "",
      displayText: ""
    };
  }
  if (String(kind || "").trim().toLowerCase() === "variable") {
    const token = unwrapTemplateToken(cleaned);
    return {
      text: `{{${token}}}`,
      displayText: token
    };
  }
  return {
    text: cleaned,
    displayText: cleaned
  };
}

function buildPointLabelData(nodes, labelOverrides, stageSize, options = {}) {
  const sorted = [...nodes].sort((a, b) => {
    if (Math.abs(a.y - b.y) > 16) return a.y - b.y;
    return a.x - b.x;
  });
  const defaultLabels = {};
  const defaultLabelSources = {};
  const suggestionsWithPositions = (Array.isArray(options?.suggestedPoints) ? options.suggestedPoints : [])
    .map((item) => ({
      label: String(item?.label || "").trim(),
      pos: normalizeSuggestedPosition(item?.pos)
    }))
    .filter((item) => item.label && item.pos);
  const suggestedLabelPool = uniqueLabels([
    ...suggestionsWithPositions.map((item) => item.label),
    ...(Array.isArray(options?.suggestedLabels) ? options.suggestedLabels : [])
  ]);
  const usedLabels = new Set();

  const remainingNodes = [...sorted];
  const remainingSuggestions = [...suggestionsWithPositions];
  while (remainingNodes.length && remainingSuggestions.length) {
    let best = null;
    remainingNodes.forEach((node) => {
      const [x, y] = normalizePosition(node, stageSize);
      remainingSuggestions.forEach((suggestion) => {
        const dx = x - suggestion.pos.x;
        const dy = y - suggestion.pos.y;
        const distanceScore = Math.hypot(dx, dy);
        if (!best || distanceScore < best.distanceScore) {
          best = {
            nodeId: node.id,
            label: suggestion.label,
            distanceScore
          };
        }
      });
    });
    if (!best) break;

    defaultLabels[best.nodeId] = best.label;
    defaultLabelSources[best.nodeId] = "suggested";
    usedLabels.add(best.label.toLowerCase());
    const nodeIndex = remainingNodes.findIndex((item) => item.id === best.nodeId);
    if (nodeIndex >= 0) remainingNodes.splice(nodeIndex, 1);
    const suggestionIndex = remainingSuggestions.findIndex(
      (item) => item.label.toLowerCase() === best.label.toLowerCase()
    );
    if (suggestionIndex >= 0) remainingSuggestions.splice(suggestionIndex, 1);
  }

  let fallbackIndex = 0;
  let centerFallbackIndex = 0;
  remainingNodes.forEach((node) => {
    const nextSuggested = suggestedLabelPool.find((label) => !usedLabels.has(label.toLowerCase()));
    if (nextSuggested) {
      defaultLabels[node.id] = nextSuggested;
      defaultLabelSources[node.id] = "suggested";
      usedLabels.add(nextSuggested.toLowerCase());
      return;
    }

    let fallbackLabel =
      node.role === "circle_center"
        ? buildCenterFallbackLabel(centerFallbackIndex)
        : buildFallbackLabel(fallbackIndex);
    while (usedLabels.has(fallbackLabel.toLowerCase())) {
      if (node.role === "circle_center") {
        centerFallbackIndex += 1;
        fallbackLabel = buildCenterFallbackLabel(centerFallbackIndex);
      } else {
        fallbackIndex += 1;
        fallbackLabel = buildFallbackLabel(fallbackIndex);
      }
    }
    defaultLabels[node.id] = fallbackLabel;
    defaultLabelSources[node.id] = "fallback";
    usedLabels.add(fallbackLabel.toLowerCase());
    if (node.role === "circle_center") {
      centerFallbackIndex += 1;
    } else {
      fallbackIndex += 1;
    }
  });

  return nodes.reduce(
    (acc, node) => {
    const override = String(labelOverrides?.[node.id] || "").trim();
    const kind = String(options?.pointLabelKinds?.[node.id] || "constant").trim().toLowerCase();
    const chosen = override || defaultLabels[node.id];
      acc.labels[node.id] = buildTemplateLabel(chosen, kind).text || defaultLabels[node.id];
      acc.sources[node.id] = override ? "override" : defaultLabelSources[node.id] || "fallback";
      acc.explicit[node.id] = Boolean(override) || acc.sources[node.id] === "suggested";
      return acc;
    },
    { labels: {}, sources: {}, explicit: {} }
  );
}

function buildSegmentTopology(rawSegments, nodes) {
  const sourceSegments = Array.isArray(rawSegments) ? rawSegments : [];
  const sourceNodes = Array.isArray(nodes) ? nodes : [];
  const supportSegments = [];
  const segments = [];
  const onSegmentRelations = [];
  const collinearRelations = [];
  const collinearKeys = new Set();
  const extensionSnaps = new Map();

  function pushCollinearRelation(pointIds, refs = [], reason = "support_line") {
    const cleanedPointIds = [...new Set((Array.isArray(pointIds) ? pointIds : []).filter(Boolean))];
    if (cleanedPointIds.length < 3) return;
    const forward = cleanedPointIds.join("|");
    const reverse = [...cleanedPointIds].reverse().join("|");
    const key = forward < reverse ? forward : reverse;
    if (collinearKeys.has(key)) return;
    collinearKeys.add(key);
    collinearRelations.push({
      type: "collinear",
      pointIds: cleanedPointIds,
      refs: (Array.isArray(refs) ? refs : []).filter(Boolean),
      reason
    });
  }

  sourceSegments.forEach((segment) => {
    const from = segment.from;
    const to = segment.to;
    const supportSegment = {
      ...segment,
      supportId: segment.id,
      isSupport: true,
      order: Number(segment.order || 0),
      start: { x: from.x, y: from.y },
      end: { x: to.x, y: to.y }
    };
    supportSegments.push(supportSegment);

    const span = distance(from, to);
    const maxProjectionDistance = clamp(span * 0.045, 6, 12);
    const candidateInteriorNodes = sourceNodes
      .filter((node) => node.id !== from.id && node.id !== to.id)
      .map((node) => {
        const projection = projectPointOntoSegment(node, from, to);
        if (projection.distance > maxProjectionDistance) return null;
        if (projection.rawT <= 0.08 || projection.rawT >= 0.92) return null;
        return {
          node,
          t: projection.t,
          projectionPoint: projection.point
        };
      })
      .filter(Boolean)
      .sort((left, right) => left.t - right.t)
      .filter((item, index, list) => {
        if (!index) return true;
        const prev = list[index - 1];
        return item.node.id !== prev.node.id && Math.abs(item.t - prev.t) > 0.035;
      });

    const topologyInteriorNodes = candidateInteriorNodes;
    const splitInteriorNodes = topologyInteriorNodes.filter((item) => {
      const nodeSplitOrder = Number.isFinite(Number(item?.node?.splitOrder)) ? Number(item.node.splitOrder) : Infinity;
      return nodeSplitOrder <= supportSegment.order;
    });

    const topologyOrderedNodes = [from, ...topologyInteriorNodes.map((item) => item.node), to];
    const splitOrderedNodes = [from, ...splitInteriorNodes.map((item) => item.node), to];
    supportSegment.orderedNodeIds = topologyOrderedNodes.map((node) => node.id);

    if (topologyInteriorNodes.length) {
      topologyInteriorNodes.forEach((item) => {
        if (item.projectionPoint) {
          item.node.x = item.projectionPoint.x;
          item.node.y = item.projectionPoint.y;
        }
        onSegmentRelations.push({
          type: "on_segment",
          pointId: item.node.id,
          refs: [supportSegment.id]
        });
      });
      pushCollinearRelation(
        topologyOrderedNodes.map((node) => node.id),
        [supportSegment.id],
        "support_line"
      );
    }

    for (let index = 0; index < splitOrderedNodes.length - 1; index += 1) {
      const startNode = splitOrderedNodes[index];
      const endNode = splitOrderedNodes[index + 1];
      if (distance(startNode, endNode) < 6) continue;
      segments.push({
        id: splitInteriorNodes.length ? `${segment.id}_part_${index + 1}` : segment.id,
        from: startNode,
        to: endNode,
        color: String(segment.color || "#0f172a"),
        strokeStyle: segment.strokeStyle,
        dash: Array.isArray(segment.dash) ? [...segment.dash] : undefined,
        confidence: segment.confidence,
        start: { x: startNode.x, y: startNode.y },
        end: { x: endNode.x, y: endNode.y },
        supportId: segment.id
      });
    }
  });

  const incidentSupportSegments = new Map();
  supportSegments.forEach((segment) => {
    [segment.from.id, segment.to.id].forEach((pointId) => {
      if (!pointId) return;
      const existing = incidentSupportSegments.get(pointId) || [];
      existing.push(segment);
      incidentSupportSegments.set(pointId, existing);
    });
  });

  incidentSupportSegments.forEach((incident, vertexId) => {
    if (!vertexId || !Array.isArray(incident) || incident.length < 2) return;
    const vertex = sourceNodes.find((node) => node.id === vertexId);
    if (!vertex) return;

    for (let index = 0; index < incident.length; index += 1) {
      for (let nextIndex = index + 1; nextIndex < incident.length; nextIndex += 1) {
        const segmentA = incident[index];
        const segmentB = incident[nextIndex];
        const otherPointA = segmentA.from.id === vertexId ? segmentA.to : segmentA.from;
        const otherPointB = segmentB.from.id === vertexId ? segmentB.to : segmentB.from;
        if (!otherPointA?.id || !otherPointB?.id || otherPointA.id === otherPointB.id) continue;

        const directionA = normalizeVector({
          x: otherPointA.x - vertex.x,
          y: otherPointA.y - vertex.y
        });
        const directionB = normalizeVector({
          x: otherPointB.x - vertex.x,
          y: otherPointB.y - vertex.y
        });
        if (!directionA || !directionB) continue;

        const straightness = Math.abs(180 - angleBetweenVectors(directionA, directionB));
        if (straightness > 34) continue;

        const extensionProjection = projectPointOntoSegment(vertex, otherPointA, otherPointB);
        const snapDistance = distance(vertex, extensionProjection.point);
        const extensionSnapLimit = clamp(
          Math.min(distance(vertex, otherPointA), distance(vertex, otherPointB)) * 0.22,
          14,
          24
        );
        if (snapDistance <= extensionSnapLimit) {
          const vertexSplitOrder = Number.isFinite(Number(vertex?.splitOrder)) ? Number(vertex.splitOrder) : Infinity;
          const newestIncidentOrder = Math.max(Number(segmentA.order || 0), Number(segmentB.order || 0));
          if (vertex.locked || vertexSplitOrder < newestIncidentOrder) {
            pushCollinearRelation(
              [otherPointA.id, vertexId, otherPointB.id],
              [segmentA.id, segmentB.id],
              "extension"
            );
            continue;
          }
          const existingSnap = extensionSnaps.get(vertexId);
          if (existingSnap) {
            existingSnap.x += extensionProjection.point.x;
            existingSnap.y += extensionProjection.point.y;
            existingSnap.count += 1;
          } else {
            extensionSnaps.set(vertexId, {
              x: extensionProjection.point.x,
              y: extensionProjection.point.y,
              count: 1
            });
          }
        }

        pushCollinearRelation(
          [otherPointA.id, vertexId, otherPointB.id],
          [segmentA.id, segmentB.id],
          "extension"
        );
      }
    }
  });

  extensionSnaps.forEach((snap, vertexId) => {
    const vertex = sourceNodes.find((node) => node.id === vertexId);
    if (!vertex || vertex.locked || !Number.isFinite(snap?.count) || snap.count <= 0) return;
    vertex.x = snap.x / snap.count;
    vertex.y = snap.y / snap.count;
  });

  return {
    segments,
    supportSegments,
    onSegmentRelations,
    collinearRelations
  };
}

function segmentConnects(segment, pointAId, pointBId) {
  if (!segment || !pointAId || !pointBId) return false;
  const fromId = segment.from?.id;
  const toId = segment.to?.id;
  return (
    (fromId === pointAId && toId === pointBId) ||
    (fromId === pointBId && toId === pointAId)
  );
}

function buildMidpointDependencies(equalLengthDependencies, segments, supportSegments) {
  const segmentMap = new Map(
    [...(Array.isArray(supportSegments) ? supportSegments : []), ...(Array.isArray(segments) ? segments : [])].map(
      (segment) => [segment.id, segment]
    )
  );
  const dependencies = [];
  const seen = new Set();

  (Array.isArray(equalLengthDependencies) ? equalLengthDependencies : []).forEach((dependency) => {
    if (!Array.isArray(dependency?.refs) || dependency.refs.length !== 2) return;
    const [refA, refB] = dependency.refs;
    const segmentA = segmentMap.get(refA);
    const segmentB = segmentMap.get(refB);
    if (!segmentA || !segmentB) return;

    const supportIdA = segmentA.supportId || segmentA.id;
    const supportIdB = segmentB.supportId || segmentB.id;
    if (!supportIdA || supportIdA !== supportIdB) return;

    const sharedPointId = [segmentA.from.id, segmentA.to.id].find(
      (pointId) => pointId === segmentB.from.id || pointId === segmentB.to.id
    );
    if (!sharedPointId) return;

    const supportSegment = segmentMap.get(supportIdA);
    if (!supportSegment) return;

    const key = `${sharedPointId}:${supportSegment.id}`;
    if (seen.has(key)) return;
    seen.add(key);
    dependencies.push({
      type: "midpoint",
      pointId: sharedPointId,
      refs: [supportSegment.id],
      supportRefs: [segmentA.id, segmentB.id],
      tickCount: dependency.tickCount,
      confidence: Number(dependency.confidence || 0.5)
    });
  });

  return dependencies;
}

function buildMedianDependencies(midpointDependencies, segments, supportSegments) {
  const topologySegments = Array.isArray(segments) ? segments : [];
  const allSegments = [
    ...(Array.isArray(supportSegments) ? supportSegments : []),
    ...topologySegments
  ];
  const supportMap = new Map((Array.isArray(supportSegments) ? supportSegments : []).map((segment) => [segment.id, segment]));
  const dependencies = [];
  const seen = new Set();

  (Array.isArray(midpointDependencies) ? midpointDependencies : []).forEach((dependency) => {
    const supportId = Array.isArray(dependency?.refs) ? dependency.refs[0] : null;
    const supportSegment = supportMap.get(supportId);
    if (!supportSegment) return;

    const midpointId = dependency.pointId;
    const baseStartId = supportSegment.from.id;
    const baseEndId = supportSegment.to.id;
    const candidateSegments = topologySegments.filter((segment) => {
      const touchesMidpoint = segment.from.id === midpointId || segment.to.id === midpointId;
      const sameSupport = (segment.supportId || segment.id) === supportSegment.id;
      return touchesMidpoint && !sameSupport;
    });

    candidateSegments.forEach((segment) => {
      const vertexId = segment.from.id === midpointId ? segment.to.id : segment.from.id;
      if (!vertexId || vertexId === baseStartId || vertexId === baseEndId) return;
      const hasSideToStart = allSegments.some((item) => segmentConnects(item, vertexId, baseStartId));
      const hasSideToEnd = allSegments.some((item) => segmentConnects(item, vertexId, baseEndId));
      if (!hasSideToStart || !hasSideToEnd) return;

      const key = `${segment.id}:${supportSegment.id}`;
      if (seen.has(key)) return;
      seen.add(key);
      dependencies.push({
        type: "median",
        pointId: midpointId,
        refs: [segment.id, supportSegment.id],
        vertexId,
        confidence: Number(dependency.confidence || 0.5)
      });
    });
  });

  return dependencies;
}

function buildTriangleRelations(segments) {
  const adjacency = new Map();
  (Array.isArray(segments) ? segments : []).forEach((segment) => {
    const a = segment.from.id;
    const b = segment.to.id;
    if (!adjacency.has(a)) adjacency.set(a, new Set());
    if (!adjacency.has(b)) adjacency.set(b, new Set());
    adjacency.get(a).add(b);
    adjacency.get(b).add(a);
  });

  const nodeIds = [...adjacency.keys()];
  const relations = [];
  const seen = new Set();
  for (let i = 0; i < nodeIds.length; i += 1) {
    for (let j = i + 1; j < nodeIds.length; j += 1) {
      for (let k = j + 1; k < nodeIds.length; k += 1) {
        const a = nodeIds[i];
        const b = nodeIds[j];
        const c = nodeIds[k];
        const connected =
          adjacency.get(a)?.has(b) &&
          adjacency.get(a)?.has(c) &&
          adjacency.get(b)?.has(c);
        if (connected) {
          const refs = [a, b, c];
          const key = [...refs].sort().join(":");
          if (seen.has(key)) continue;
          seen.add(key);
          relations.push({ type: "triangle", refs });
        }
      }
    }
  }
  return relations;
}

function lookupNode(nodeMap, id) {
  return id ? nodeMap.get(id) || null : null;
}

function buildCircleSuggestions(segments, triangleRelations) {
  const nodeMap = new Map();
  segments.forEach((segment) => {
    nodeMap.set(segment.from.id, segment.from);
    nodeMap.set(segment.to.id, segment.to);
  });

  const suggestions = triangleRelations
    .flatMap((relation, index) => {
      const [idA, idB, idC] = relation.refs || [];
      const pointA = lookupNode(nodeMap, idA);
      const pointB = lookupNode(nodeMap, idB);
      const pointC = lookupNode(nodeMap, idC);
      if (!pointA || !pointB || !pointC) return [];

      const sideA = distance(pointB, pointC);
      const sideB = distance(pointA, pointC);
      const sideC = distance(pointA, pointB);
      const perimeter = sideA + sideB + sideC;
      if (!perimeter) return [];

      const area =
        Math.abs(
          pointA.x * (pointB.y - pointC.y) +
            pointB.x * (pointC.y - pointA.y) +
            pointC.x * (pointA.y - pointB.y)
        ) / 2;
      if (!area) return [];

      const suggestions = [];
      const center = {
        x: (sideA * pointA.x + sideB * pointB.x + sideC * pointC.x) / perimeter,
        y: (sideA * pointA.y + sideB * pointB.y + sideC * pointC.y) / perimeter
      };
      const radius = (2 * area) / perimeter;
      if (Number.isFinite(radius) && radius > 6) {
        suggestions.push({
          id: `triangle_${index + 1}_incircle`,
          type: "incircle",
          label: "Incircle",
          triangleRefs: [idA, idB, idC],
          center,
          radius
        });
      }

      const ax = pointA.x;
      const ay = pointA.y;
      const bx = pointB.x;
      const by = pointB.y;
      const cx = pointC.x;
      const cy = pointC.y;
      const denominator = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
      if (Math.abs(denominator) > 1e-6) {
        const aSq = ax * ax + ay * ay;
        const bSq = bx * bx + by * by;
        const cSq = cx * cx + cy * cy;
        const circumCenter = {
          x: (aSq * (by - cy) + bSq * (cy - ay) + cSq * (ay - by)) / denominator,
          y: (aSq * (cx - bx) + bSq * (ax - cx) + cSq * (bx - ax)) / denominator
        };
        const circumRadius = distance(circumCenter, pointA);
        if (Number.isFinite(circumRadius) && circumRadius > 6) {
          suggestions.push({
            id: `triangle_${index + 1}_circumcircle`,
            type: "circumcircle",
            label: "Circumcircle",
            triangleRefs: [idA, idB, idC],
            center: circumCenter,
            radius: circumRadius
          });
        }
      }

      return suggestions;
    })
    .filter(Boolean);

  const seen = new Set();
  return suggestions.filter((suggestion) => {
    const triangleKey = (Array.isArray(suggestion?.triangleRefs) ? suggestion.triangleRefs : [])
      .map((ref) => String(ref || "").trim())
      .filter(Boolean)
      .sort()
      .join(":");
    const key = `${String(suggestion?.type || "").trim().toLowerCase()}:${triangleKey}`;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function buildSegmentLabelMap(segments, pointLabels) {
  return (Array.isArray(segments) ? segments : []).reduce((acc, segment) => {
    acc[segment.id] = `${pointLabels[segment.from.id] || segment.from.id}${pointLabels[segment.to.id] || segment.to.id}`;
    return acc;
  }, {});
}

function buildRightAngleMarkers(dependencies, segments, nodes) {
  const segmentMap = new Map((Array.isArray(segments) ? segments : []).map((segment) => [segment.id, segment]));
  const nodeMap = new Map((Array.isArray(nodes) ? nodes : []).map((node) => [node.id, node]));

  return (Array.isArray(dependencies) ? dependencies : [])
    .filter((dependency) => dependency?.type === "perpendicular")
    .map((dependency, index) => {
      if (Array.isArray(dependency?.rightAnglePoints) && dependency.rightAnglePoints.length === 3) {
        return {
          id: `right_angle_${index + 1}`,
          pointId: dependency.pointId,
          refs: dependency.refs,
          points: dependency.rightAnglePoints
        };
      }
      const vertex = nodeMap.get(dependency.pointId);
      const [segmentA, segmentB] = (dependency.refs || []).map((ref) => segmentMap.get(ref));
      if (!vertex || !segmentA || !segmentB) return null;

      const directionA = getSegmentDirectionFromNode(segmentA, vertex);
      const directionB = getSegmentDirectionFromNode(segmentB, vertex);
      if (!directionA || !directionB) return null;

      const spanA =
        segmentA.from.id === vertex.id
          ? distance(segmentA.from, segmentA.to)
          : distance(segmentA.to, segmentA.from);
      const spanB =
        segmentB.from.id === vertex.id
          ? distance(segmentB.from, segmentB.to)
          : distance(segmentB.to, segmentB.from);
      const size = clamp(Math.min(spanA, spanB) * 0.16, 10, 18);
      const first = {
        x: vertex.x + directionA.x * size,
        y: vertex.y + directionA.y * size
      };
      const third = {
        x: vertex.x + directionB.x * size,
        y: vertex.y + directionB.y * size
      };
      const corner = {
        x: first.x + directionB.x * size,
        y: first.y + directionB.y * size
      };

      return {
        id: `right_angle_${index + 1}`,
        pointId: dependency.pointId,
        refs: dependency.refs,
        points: [first, corner, third]
      };
    })
    .filter(Boolean);
}

function canonicalDependencyKey(dependency) {
  const type = String(dependency?.type || "").trim().toLowerCase();
  const pointId = String(dependency?.pointId || "").trim();
  const refs = (Array.isArray(dependency?.refs) ? dependency.refs : [])
    .map((ref) => String(ref || "").trim())
    .filter(Boolean)
    .sort();
  if (!type) return "";
  return [type, pointId, ...refs].join("|");
}

function dedupeDependencies(dependencies) {
  const deduped = [];
  const seen = new Set();
  (Array.isArray(dependencies) ? dependencies : []).forEach((dependency) => {
    const key = canonicalDependencyKey(dependency);
    if (!key) {
      deduped.push(dependency);
      return;
    }
    if (seen.has(key)) return;
    seen.add(key);
    deduped.push(dependency);
  });
  return deduped;
}

function buildAngleArcMarkers(dependencies, segments, nodes) {
  const segmentMap = new Map((Array.isArray(segments) ? segments : []).map((segment) => [segment.id, segment]));
  const nodeMap = new Map((Array.isArray(nodes) ? nodes : []).map((node) => [node.id, node]));

  return (Array.isArray(dependencies) ? dependencies : [])
    .filter((dependency) => dependency?.type === "angle_mark")
    .flatMap((dependency, index) => {
      const vertex = nodeMap.get(dependency.pointId);
      const [segmentA, segmentB] = (dependency.refs || []).map((ref) => segmentMap.get(ref));
      if (!vertex || !segmentA || !segmentB) return [];

      const directionA = getSegmentDirectionFromNode(segmentA, vertex);
      const directionB = getSegmentDirectionFromNode(segmentB, vertex);
      if (!directionA || !directionB) return [];

      const angleA = vectorAngle(directionA);
      const delta = shortestSignedAngle(angleA, vectorAngle(directionB));
      const sampleCount = 10;
      const radii = (Array.isArray(dependency.radii) && dependency.radii.length
        ? dependency.radii
        : [Number(dependency.radius) || 12]
      ).map((radius) => clamp(Number(radius) || 12, 8, 32));

      return radii.map((radius, radiusIndex) => {
        const points = [];
        for (let step = 0; step <= sampleCount; step += 1) {
          const t = step / sampleCount;
          const angle = angleA + delta * t;
          points.push({
            x: vertex.x + Math.cos(angle) * radius,
            y: vertex.y + Math.sin(angle) * radius
          });
        }

        return {
          id: `angle_arc_${index + 1}_${radiusIndex + 1}`,
          familyId: dependency.id,
          pointId: dependency.pointId,
          refs: dependency.refs,
          arcCount: Number(dependency.arcCount) || radii.length,
          points
        };
      });
    })
    .filter(Boolean);
}

function buildArcSamplePoints(center, radius, startAngle, sweepAngle, sampleCount = 18) {
  const steps = Math.max(8, Number(sampleCount) || 18);
  const safeRadius = Math.max(1, Number(radius) || 1);
  const points = [];
  for (let step = 0; step <= steps; step += 1) {
    const t = step / steps;
    const angle = Number(startAngle || 0) + Number(sweepAngle || 0) * t;
    points.push({
      x: center.x + Math.cos(angle) * safeRadius,
      y: center.y + Math.sin(angle) * safeRadius
    });
  }
  return points;
}

function findNearestNode(nodes, point, threshold = 16) {
  let best = null;
  (Array.isArray(nodes) ? nodes : []).forEach((node) => {
    const gap = distance(node, point);
    if (gap > threshold) return;
    if (!best || gap < best.gap) {
      best = {
        node,
        gap
      };
    }
  });
  return best?.node || null;
}

function projectPointToChordBisector(startPoint, endPoint, rawCenter, fallbackCenter) {
  const chordMidpoint = {
    x: (startPoint.x + endPoint.x) / 2,
    y: (startPoint.y + endPoint.y) / 2
  };
  const chordVector = normalizeVector({
    x: endPoint.x - startPoint.x,
    y: endPoint.y - startPoint.y
  });
  if (!chordVector) {
    return fallbackCenter || rawCenter;
  }
  const perpendicular = {
    x: -chordVector.y,
    y: chordVector.x
  };
  const fallbackOffset = {
    x: (fallbackCenter?.x || chordMidpoint.x) - chordMidpoint.x,
    y: (fallbackCenter?.y || chordMidpoint.y) - chordMidpoint.y
  };
  let offset = (Number(rawCenter?.x || chordMidpoint.x) - chordMidpoint.x) * perpendicular.x +
    (Number(rawCenter?.y || chordMidpoint.y) - chordMidpoint.y) * perpendicular.y;
  if (Math.abs(offset) < 2.4) {
    const fallbackSignedOffset = fallbackOffset.x * perpendicular.x + fallbackOffset.y * perpendicular.y;
    offset = fallbackSignedOffset >= 0 ? 2.4 : -2.4;
  }
  const maxOffset = Math.max(18, distance(startPoint, endPoint) * 2.8);
  const clampedOffset = clamp(offset, -maxOffset, maxOffset);
  return {
    x: chordMidpoint.x + perpendicular.x * clampedOffset,
    y: chordMidpoint.y + perpendicular.y * clampedOffset
  };
}

function resolveArcStrokeGeometry(stroke, centerOverride = null) {
  if (!stroke?.start || !stroke?.end || !stroke?.midpoint || !stroke?.center) return null;
  const center = centerOverride
    ? projectPointToChordBisector(stroke.start, stroke.end, centerOverride, stroke.center)
    : stroke.center;
  const radius = distance(center, stroke.start);
  if (!Number.isFinite(radius) || radius < 8) return null;

  const sweep = resolveArcSweep(center, stroke.start, stroke.midpoint, stroke.end);
  if (!sweep || sweep.sweepDegrees < 12 || sweep.sweepDegrees > 340) return null;

  return {
    center,
    radius,
    startPoint: stroke.start,
    endPoint: stroke.end,
    midpoint: stroke.midpoint,
    startAngle: sweep.startAngle,
    endAngle: sweep.endAngle,
    sweepAngle: sweep.sweepAngle,
    sweepDegrees: sweep.sweepDegrees,
    points: buildArcSamplePoints(center, radius, sweep.startAngle, sweep.sweepAngle)
  };
}

function buildAngleEntities(dependencies, segments, pointLabels) {
  const segmentMap = new Map((Array.isArray(segments) ? segments : []).map((segment) => [segment.id, segment]));
  const seen = new Set();

  return (Array.isArray(dependencies) ? dependencies : [])
    .filter((dependency) => dependency?.type === "angle_mark")
    .map((dependency) => {
      const [segmentA, segmentB] = (dependency.refs || []).map((ref) => segmentMap.get(ref));
      if (!segmentA || !segmentB) return null;
      const vertexId = dependency.pointId;
      const sideA = getOtherEndpoint(segmentA, { id: vertexId });
      const sideB = getOtherEndpoint(segmentB, { id: vertexId });
      if (!sideA || !sideB) return null;
      const pointA = pointLabels[sideA.id] || sideA.id;
      const vertex = pointLabels[vertexId] || vertexId;
      const pointB = pointLabels[sideB.id] || sideB.id;
      const label = `${pointA}${vertex}${pointB}`;
      const key = label.toLowerCase();
      if (seen.has(key)) return null;
      seen.add(key);
      return {
        id: label,
        label,
        type: "angle",
        points: [pointA, vertex, pointB]
      };
    })
    .filter(Boolean);
}

function canonicalAngleCandidateId(pointAId, vertexId, pointBId) {
  const endpoints = [String(pointAId || "").trim(), String(pointBId || "").trim()].sort((left, right) =>
    left.localeCompare(right)
  );
  return [endpoints[0] || "", String(vertexId || "").trim(), endpoints[1] || ""].join("__");
}

function buildAngleCandidates(dependencies, segments, pointLabels) {
  const segmentMap = new Map((Array.isArray(segments) ? segments : []).map((segment) => [segment.id, segment]));
  const nodeMap = new Map();
  (Array.isArray(segments) ? segments : []).forEach((segment) => {
    [segment?.from, segment?.to].forEach((point) => {
      if (point?.id && !nodeMap.has(point.id)) nodeMap.set(point.id, point);
    });
  });

  const seen = new Set();
  return (Array.isArray(dependencies) ? dependencies : [])
    .filter((dependency) => dependency?.type === "angle_mark")
    .map((dependency) => {
      const [segmentA, segmentB] = (dependency.refs || []).map((ref) => segmentMap.get(ref));
      const vertex = nodeMap.get(dependency.pointId);
      const pointIds = Array.isArray(dependency.anglePointIds) ? dependency.anglePointIds : [];
      if (!segmentA || !segmentB || !vertex || pointIds.length < 3) return null;

      const directionA = getSegmentDirectionFromNode(segmentA, vertex);
      const directionB = getSegmentDirectionFromNode(segmentB, vertex);
      if (!directionA || !directionB) return null;

      let bisector = normalizeVector({
        x: directionA.x + directionB.x,
        y: directionA.y + directionB.y
      });
      if (!bisector) {
        bisector = normalizeVector({
          x: directionA.y,
          y: -directionA.x
        });
      }
      if (!bisector) return null;

      const candidateId = canonicalAngleCandidateId(pointIds[0], pointIds[1], pointIds[2]);
      if (seen.has(candidateId)) return null;
      seen.add(candidateId);

      const candidateRadius = clamp(
        Math.max(
          12,
          Number(dependency.radius || 0) + 8,
          Array.isArray(dependency.radii) && dependency.radii.length
            ? Math.max(...dependency.radii.map((value) => Number(value) || 0)) + 6
            : 0
        ),
        12,
        34
      );

      return {
        id: candidateId,
        familyId: dependency.id,
        type: "angle",
        pointIds,
        points: pointIds.map((pointId) => pointLabels[pointId] || pointId),
        label: pointIds.map((pointId) => pointLabels[pointId] || pointId).join(""),
        position: {
          x: vertex.x + bisector.x * candidateRadius,
          y: vertex.y + bisector.y * candidateRadius
        },
        radius: Number(candidateRadius.toFixed(2)),
        arcCount: Number(dependency.arcCount) || 1
      };
    })
    .filter(Boolean);
}

function buildSegmentLabelAnnotations(segments, pointLabels, overrides = {}) {
  const targets = Array.isArray(segments) ? segments : [];
  return targets
    .map((segment) => {
      const override = overrides?.[segment.id];
      const rawValue = String(override?.text || "").trim();
      if (!rawValue) return null;
      const kind = String(override?.kind || "constant").trim().toLowerCase() || "constant";
      const template = buildTemplateLabel(rawValue, kind);
      const midpoint = {
        x: (segment.from.x + segment.to.x) / 2,
        y: (segment.from.y + segment.to.y) / 2
      };
      const dx = segment.to.x - segment.from.x;
      const dy = segment.to.y - segment.from.y;
      const length = Math.hypot(dx, dy) || 1;
      const offset = {
        x: (-dy / length) * 12,
        y: (dx / length) * 12
      };
      return {
        id: `segment_label_${segment.id}`,
        targetType: "segment",
        targetRef: segment.id,
        segment: segment.id,
        kind,
        rawText: rawValue,
        text: template.text,
        displayText: template.displayText,
        position: {
          x: midpoint.x + offset.x,
          y: midpoint.y + offset.y
        },
        anchorPosition: midpoint,
        description: `${pointLabels[segment.from.id] || segment.from.id}${pointLabels[segment.to.id] || segment.to.id}`,
        refs: [`${pointLabels[segment.from.id] || segment.from.id}${pointLabels[segment.to.id] || segment.to.id}`]
      };
    })
    .filter(Boolean);
}

function buildLogicalSegmentLabelTargets(supportSegments, nodes, visiblePointLabels = {}, overrides = {}) {
  const deduped = new Map();
  const sourceNodes = Array.isArray(nodes) ? nodes : [];

  (Array.isArray(supportSegments) ? supportSegments : []).forEach((supportSegment) => {
    const fromNode = supportSegment?.from;
    const toNode = supportSegment?.to;
    if (!fromNode?.id || !toNode?.id) return;
    const span = distance(fromNode, toNode);
    if (span < 6) return;

    const maxProjectionDistance = clamp(span * 0.06, 8, 16);
    const orderedNodes = sourceNodes
      .filter((node) => String(visiblePointLabels?.[node.id] || "").trim())
      .map((node) => {
        if (node.id === fromNode.id) {
          return { node, t: 0 };
        }
        if (node.id === toNode.id) {
          return { node, t: 1 };
        }
        const projection = projectPointOntoSegment(node, fromNode, toNode);
        if (projection.distance > maxProjectionDistance) return null;
        if (projection.rawT <= 0.02 || projection.rawT >= 0.98) return null;
        return {
          node,
          t: projection.t
        };
      })
      .filter(Boolean)
      .sort((left, right) => left.t - right.t)
      .filter((item, index, list) => {
        if (!index) return true;
        const previous = list[index - 1];
        return item.node.id !== previous.node.id && Math.abs(item.t - previous.t) > 0.02;
      })
      .map((item) => item.node);

    if (orderedNodes.length < 2) return;

    for (let index = 0; index < orderedNodes.length - 1; index += 1) {
      const from = orderedNodes[index];
      const to = orderedNodes[index + 1];
      if (!from?.id || !to?.id || from.id === to.id) continue;
      const fromLabel = String(visiblePointLabels?.[from.id] || "").trim();
      const toLabel = String(visiblePointLabels?.[to.id] || "").trim();
      if (!fromLabel || !toLabel) continue;

      const isOriginalEndpoints =
        orderedNodes.length === 2 &&
        orderedNodes[0]?.id === from.id &&
        orderedNodes[1]?.id === to.id;
      const targetId = isOriginalEndpoints
        ? supportSegment.id
        : `${supportSegment.id}__${from.id}__${to.id}`;
      const dedupeKey = [fromLabel, toLabel].sort().join("|");
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const length = Math.hypot(dx, dy) || 1;
      const candidate = {
        id: targetId,
        from,
        to,
        supportId: supportSegment.id,
        label: `${fromLabel}${toLabel}`,
        text: String(overrides?.[targetId]?.text || "").trim(),
        kind: String(overrides?.[targetId]?.kind || "constant").trim().toLowerCase() || "constant",
        x: Number((((from.x + to.x) / 2) + ((from.y - to.y) / length) * 12).toFixed(2)),
        y: Number((((from.y + to.y) / 2) + ((to.x - from.x) / length) * 12).toFixed(2))
      };
      const existing = deduped.get(dedupeKey);
      if (!existing || (!existing.text && candidate.text)) {
        deduped.set(dedupeKey, candidate);
      }
    }
  });

  return [...deduped.values()];
}

function buildSegmentLabelCandidates(
  supportSegments,
  nodes,
  visiblePointLabels = {},
  overrides = {}
) {
  return buildLogicalSegmentLabelTargets(supportSegments, nodes, visiblePointLabels, overrides);
}

function buildArcDisplayLabel(arc, pointLabels, fallbackIndex) {
  const startLabel = arc?.startNodeId ? pointLabels[arc.startNodeId] || arc.startNodeId : "";
  const endLabel = arc?.endNodeId ? pointLabels[arc.endNodeId] || arc.endNodeId : "";
  if (startLabel && endLabel) return `arc ${startLabel}${endLabel}`;
  return `arc ${fallbackIndex + 1}`;
}

function buildCircleDisplayLabel(circle, fallbackIndex) {
  const explicitLabel = String(circle?.label || "").trim();
  if (explicitLabel) return explicitLabel;
  return String(circle?.id || `circle_${fallbackIndex + 1}`).trim() || `circle_${fallbackIndex + 1}`;
}

function buildSupportSegmentDisplayLabel(segment, nodeMap, pointLabels) {
  const orderedNodes = (Array.isArray(segment?.orderedNodeIds) ? segment.orderedNodeIds : [])
    .map((nodeId) => nodeMap.get(nodeId))
    .filter(Boolean);
  const labeledNodes = orderedNodes.filter((node) => String(pointLabels?.[node.id] || "").trim());
  const labelFromNodes = labeledNodes.length >= 2
    ? `${pointLabels[labeledNodes[0].id]}${pointLabels[labeledNodes[labeledNodes.length - 1].id]}`
    : "";
  if (labelFromNodes) return labelFromNodes;
  const fromLabel = String(pointLabels?.[segment?.from?.id] || segment?.from?.id || "").trim();
  const toLabel = String(pointLabels?.[segment?.to?.id] || segment?.to?.id || "").trim();
  return `${fromLabel}${toLabel}`;
}

function buildCircularSemanticRelations(nodes, arcs, circles, pointLabels, segments = []) {
  const nodeMap = new Map((Array.isArray(nodes) ? nodes : []).map((node) => [node.id, node]));
  const relations = [];
  const seen = new Set();
  const circlePointRelations = [];

  const pushRelation = (relation) => {
    const signature = JSON.stringify({
      type: relation?.type,
      point: relation?.point || "",
      refs: Array.isArray(relation?.refs) ? relation.refs : [],
      carrier: relation?.carrier || ""
    });
    if (seen.has(signature)) return;
    seen.add(signature);
    relations.push(relation);
    if (relation?.type === "point_on_circle") {
      circlePointRelations.push({
        circleId: String(relation?.carrier || relation?.refs?.[0] || "").trim(),
        point: String(relation?.point || "").trim()
      });
    }
  };

  (Array.isArray(arcs) ? arcs : []).forEach((arc, index) => {
    const carrierLabel = buildArcDisplayLabel(arc, pointLabels, index);
    const startNode = arc?.startNodeId ? nodeMap.get(arc.startNodeId) : null;
    const endNode = arc?.endNodeId ? nodeMap.get(arc.endNodeId) : null;

    if (startNode && endNode) {
      const chordLength = distance(startNode, endNode);
      const midpoint = {
        x: (startNode.x + endNode.x) / 2,
        y: (startNode.y + endNode.y) / 2
      };
      const centerProjection = projectPointOntoSegment(arc.center, startNode, endNode);
      const projectionTolerance = Math.max(4, chordLength * 0.035);
      const midpointTolerance = Math.max(6, chordLength * 0.06);
      if (
        Number.isFinite(Number(arc?.sweepDegrees)) &&
        Math.abs(Number(arc.sweepDegrees) - 180) <= 25 &&
        Math.abs(Number(centerProjection?.rawT) - 0.5) <= 0.18 &&
        Number(centerProjection?.distance) <= projectionTolerance &&
        distance(arc.center, midpoint) <= midpointTolerance
      ) {
        const pointA = pointLabels[startNode.id] || startNode.id;
        const pointB = pointLabels[endNode.id] || endNode.id;
        pushRelation({
          type: "diameter",
          refs: [pointA, pointB],
          carrier: arc.id,
          carrierLabel,
          description: `${pointA}${pointB} is a diameter of ${carrierLabel}.`
        });
      }
    }

    const radialTolerance = clamp(arc.radius * 0.05, 3, 10);
    (Array.isArray(nodes) ? nodes : []).forEach((node) => {
      if (!node?.id || node.id === arc.startNodeId || node.id === arc.endNodeId) return;
      const radialDelta = Math.abs(distance(node, arc.center) - arc.radius);
      if (radialDelta > radialTolerance) return;
      const testAngle = vectorAngle({
        x: node.x - arc.center.x,
        y: node.y - arc.center.y
      });
      if (!sweepContainsAngle(arc.startAngle, arc.sweepAngle, testAngle, 0.2)) return;
      const point = pointLabels[node.id] || node.id;
      pushRelation({
        type: "point_on_arc",
        point,
        refs: [arc.id],
        carrier: arc.id,
        carrierLabel,
        description: `${point} lies on ${carrierLabel}.`
      });
    });
  });

  (Array.isArray(circles) ? circles : []).forEach((circle, index) => {
    const carrierLabel = buildCircleDisplayLabel(circle, index);
    const centerLabel = String(pointLabels?.[circle?.centerNode?.id] || "").trim();
    const radialTolerance = clamp(circle.radius * 0.05, 3, 10);
    (Array.isArray(nodes) ? nodes : []).forEach((node) => {
      if (!node?.id || node.id === circle?.centerNode?.id) return;
      const radialDelta = Math.abs(distance(node, circle.center) - circle.radius);
      if (radialDelta > radialTolerance) return;
      const point = pointLabels[node.id] || node.id;
      if (centerLabel && String(point).trim() === centerLabel) return;
      pushRelation({
        type: "point_on_circle",
        point,
        refs: [circle.id],
        carrier: circle.id,
        carrierLabel,
        description: `${point} lies on ${carrierLabel}.`
      });
    });
  });

  (Array.isArray(circles) ? circles : []).forEach((circle, index) => {
    const carrierLabel = buildCircleDisplayLabel(circle, index);
    const pointsOnCircle = circlePointRelations
      .filter((item) => item.circleId === circle.id)
      .map((item) => item.point)
      .filter(Boolean);
    if (!pointsOnCircle.length) return;

    const pointIdSet = new Set(pointsOnCircle);
    (Array.isArray(segments) ? segments : []).forEach((segment) => {
      if (!segment?.from?.id || !segment?.to?.id) return;
      const orderedNodes = (Array.isArray(segment?.orderedNodeIds) ? segment.orderedNodeIds : [])
        .map((nodeId) => nodeMap.get(nodeId))
        .filter(Boolean);
      if (!orderedNodes.length) return;
      if (orderedNodes.some((node) => node?.id === circle?.centerNode?.id)) return;
      const tangentNodes = orderedNodes.filter((node) => {
        if (!node?.id || node.id === circle?.centerNode?.id) return false;
        const label = String(pointLabels[node.id] || node.id).trim();
        return Boolean(label) && pointIdSet.has(label);
      });
      const uniqueTangentNodes = tangentNodes.filter(
        (node, nodeIndex, list) => list.findIndex((item) => item.id === node.id) === nodeIndex
      );
      if (uniqueTangentNodes.length !== 1) return;
      const direction = normalizeVector({
        x: segment.to.x - segment.from.x,
        y: segment.to.y - segment.from.y
      });
      if (!direction) return;
      const lineLabel = buildSupportSegmentDisplayLabel(segment, nodeMap, pointLabels);
      const tangentNode = uniqueTangentNodes[0];
      const radiusDirection = normalizeVector({
        x: tangentNode.x - circle.center.x,
        y: tangentNode.y - circle.center.y
      });
      if (!radiusDirection) return;
      if (Math.abs(direction.x * radiusDirection.x + direction.y * radiusDirection.y) > 0.1) return;
      const tangentPoint = String(pointLabels[tangentNode.id] || tangentNode.id).trim();
      if (!tangentPoint) return;
      pushRelation({
        type: "tangent",
        point: tangentPoint,
        refs: [lineLabel, circle.id],
        carrier: circle.id,
        carrierLabel,
        description: `${lineLabel} is tangent to ${carrierLabel} at ${tangentPoint}.`
      });
    });
  });

  return relations;
}

function buildArcLabelAnnotations(arcs, pointLabels, overrides = {}) {
  return (Array.isArray(arcs) ? arcs : [])
    .map((arc, index) => {
      const override = overrides?.[arc.id];
      const rawValue = String(override?.text || "").trim();
      if (!rawValue) return null;
      const kind = String(override?.kind || "constant").trim().toLowerCase() || "constant";
      const template = buildTemplateLabel(rawValue, kind);
      const label = buildArcDisplayLabel(arc, pointLabels, index);
      return {
        id: `arc_label_${arc.id}`,
        targetType: "arc",
        targetRef: arc.id,
        refs: [label],
        kind,
        rawText: rawValue,
        text: template.text,
        displayText: template.displayText,
        position: arc.labelPosition,
        anchorPosition: arc.labelPosition,
        description: label
      };
    })
    .filter(Boolean);
}

function buildAngleLabelAnnotations(angleCandidates, overrides = {}) {
  return (Array.isArray(angleCandidates) ? angleCandidates : [])
    .map((candidate) => {
      const override = overrides?.[candidate.id];
      const rawValue = String(override?.text || "").trim();
      if (!rawValue) return null;
      const kind = String(override?.kind || "constant").trim().toLowerCase() || "constant";
      const template = buildTemplateLabel(rawValue, kind);
      return {
        id: `angle_label_${candidate.id}`,
        targetType: "angle",
        refs: candidate.pointIds,
        kind,
        rawText: rawValue,
        text: template.text,
        displayText: template.displayText,
        radius: candidate.radius,
        position: candidate.position,
        anchorPosition: candidate.position,
        description: candidate.label
      };
    })
    .filter(Boolean);
}

function buildGroupedAngleMarkDependencies(matches) {
  const groups = new Map();

  (Array.isArray(matches) ? matches : []).forEach((match) => {
    const pointId = match?.pointId;
    const anglePointIds = Array.isArray(match?.anglePointIds) ? match.anglePointIds : [];
    if (!pointId || anglePointIds.length < 3) return;
    const endpoints = [anglePointIds[0], anglePointIds[2]].sort();
    const key = [pointId, ...endpoints].join(":");
    const existing = groups.get(key) || {
      id: makeMarkerFamilyId("angle_mark_group", pointId, ...endpoints),
      type: "angle_mark",
      pointId,
      refs: match.refs,
      anglePointIds: match.anglePointIds,
      radii: [],
      sourceMarkIds: [],
      confidenceTotal: 0,
      count: 0
    };
    existing.radii.push(Number(match.radius) || 12);
    existing.sourceMarkIds.push(match.sourceMarkId);
    existing.confidenceTotal += Number(match.confidence || 0.5);
    existing.count += 1;
    if (!existing.refs?.length && Array.isArray(match.refs)) {
      existing.refs = match.refs;
    }
    if (!existing.anglePointIds?.length) {
      existing.anglePointIds = match.anglePointIds;
    }
    groups.set(key, existing);
  });

  return [...groups.values()].map((group) => {
    const clusteredRadii = clusterNumericValues(group.radii, 4.2);
    const rawAverageRadius =
      group.radii.reduce((sum, value) => sum + Number(value || 0), 0) / Math.max(group.radii.length, 1);
    const arcCount = normalizeMarkerFamilyCount(clusteredRadii.length || group.count, group.count);
    const radii =
      clusteredRadii.length === arcCount
        ? clusteredRadii
        : buildMarkerFamilyRadii(rawAverageRadius, arcCount);
    return {
      id: group.id,
      type: "angle_mark",
      pointId: group.pointId,
      refs: group.refs,
      anglePointIds: group.anglePointIds,
      radii,
      arcCount,
      radius: radii.reduce((sum, value) => sum + Number(value || 0), 0) / Math.max(radii.length, 1),
      confidence: group.confidenceTotal / Math.max(group.count, 1),
      sourceMarkIds: group.sourceMarkIds
    };
  });
}

function buildEqualAngleDependencies(angleMarkDependencies) {
  const byCount = new Map();
  (Array.isArray(angleMarkDependencies) ? angleMarkDependencies : []).forEach((dependency) => {
    const arcCount = Number(dependency?.arcCount);
    if (!Number.isFinite(arcCount) || arcCount <= 0) return;
    const existing = byCount.get(arcCount) || [];
    existing.push(dependency);
    byCount.set(arcCount, existing);
  });

  const dependencies = [];
  byCount.forEach((groups, arcCount) => {
    if (groups.length < 2) return;
    dependencies.push({
      type: "equal_angle",
      arcCount,
      anglePointSets: groups.map((group) => group.anglePointIds),
      confidence:
        groups.reduce((sum, group) => sum + Number(group.confidence || 0), 0) / Math.max(groups.length, 1)
    });
  });

  return dependencies;
}

function buildLengthTickMarkers(lengthTickGroups, segments) {
  const segmentMap = new Map((Array.isArray(segments) ? segments : []).map((segment) => [segment.id, segment]));
  const markers = [];

  (Array.isArray(lengthTickGroups) ? lengthTickGroups : []).forEach((group) => {
    const segment = segmentMap.get(group.segmentId);
    if (!segment) return;

    const direction = normalizeVector({
      x: segment.to.x - segment.from.x,
      y: segment.to.y - segment.from.y
    });
    if (!direction) return;

    const perpendicular = {
      x: -direction.y,
      y: direction.x
    };
    const dashLength = clamp(group.segmentLength * 0.14, 8, 14);
    const dashSpacing = clamp(dashLength * 0.7, 5, 10);
    const startOffset = (Number(group.count || 1) - 1) / 2;

    for (let index = 0; index < Number(group.count || 1); index += 1) {
      const offset = (index - startOffset) * dashSpacing;
      const center = {
        x: group.anchorPoint.x + direction.x * offset,
        y: group.anchorPoint.y + direction.y * offset
      };
      markers.push({
        id: `${group.id}_dash_${index + 1}`,
        familyId: group.id,
        segmentId: group.segmentId,
        tickCount: group.count,
        points: [
          {
            x: center.x - perpendicular.x * (dashLength / 2),
            y: center.y - perpendicular.y * (dashLength / 2)
          },
          {
            x: center.x + perpendicular.x * (dashLength / 2),
            y: center.y + perpendicular.y * (dashLength / 2)
          }
        ]
      });
    }
  });

  return markers;
}

function buildParallelArrowMarkers(parallelArrowGroups, segments) {
  const segmentMap = new Map((Array.isArray(segments) ? segments : []).map((segment) => [segment.id, segment]));
  const markers = [];

  (Array.isArray(parallelArrowGroups) ? parallelArrowGroups : []).forEach((group) => {
    const segment = segmentMap.get(group.segmentId);
    if (!segment) return;

    const direction = normalizeVector({
      x: segment.to.x - segment.from.x,
      y: segment.to.y - segment.from.y
    });
    if (!direction) return;

    const perpendicular = {
      x: -direction.y,
      y: direction.x
    };
    const arrowDepth = clamp(group.segmentLength * 0.12, 8, 14);
    const arrowSpread = clamp(arrowDepth * 0.75, 5, 10);
    const arrowSpacing = clamp(arrowDepth * 0.8, 6, 12);
    const startOffset = (Number(group.count || 1) - 1) / 2;

    for (let index = 0; index < Number(group.count || 1); index += 1) {
      const offset = (index - startOffset) * arrowSpacing;
      const tip = {
        x: group.anchorPoint.x + direction.x * offset,
        y: group.anchorPoint.y + direction.y * offset
      };
      const tailCenter = {
        x: tip.x + direction.x * arrowDepth * Number(group.orientationSign || 1),
        y: tip.y + direction.y * arrowDepth * Number(group.orientationSign || 1)
      };
      markers.push({
        id: `${group.id}_arrow_${index + 1}`,
        familyId: group.id,
        segmentId: group.segmentId,
        arrowCount: group.count,
        points: [
          {
            x: tailCenter.x - perpendicular.x * (arrowSpread / 2),
            y: tailCenter.y - perpendicular.y * (arrowSpread / 2)
          },
          tip,
          {
            x: tailCenter.x + perpendicular.x * (arrowSpread / 2),
            y: tailCenter.y + perpendicular.y * (arrowSpread / 2)
          }
        ]
      });
    }
  });

  return markers;
}

function summarizeResult(
  pointLabels,
  segments,
  arcs,
  circles,
  angleEntities,
  dependencies,
  warnings,
  relationSummary = [],
  givenSummary = []
) {
  const entitySummary = [
    ...Object.entries(pointLabels).map(([, label]) => `Point ${label}`),
    ...segments.map((segment) => `segment ${pointLabels[segment.from.id]}${pointLabels[segment.to.id]}`),
    ...angleEntities.map((angle) => `Angle ${angle.label}`),
    ...arcs.map((arc, index) => {
      const startLabel = arc.startNodeId ? pointLabels[arc.startNodeId] || arc.startNodeId : "";
      const endLabel = arc.endNodeId ? pointLabels[arc.endNodeId] || arc.endNodeId : "";
      const label = startLabel && endLabel ? `${startLabel}${endLabel}` : arc.id || `arc ${index + 1}`;
      return `arc ${label}`;
    }),
    ...circles.map((circle, index) => {
      const centerLabel = pointLabels[circle.centerNode.id] || circle.centerNode.id;
      return `circle ${circle.label || index + 1} with center ${centerLabel}`;
    })
  ];
  const dependencySummary = dependencies.map((item) => item.description);
  return {
    entitySummary,
    relationSummary: (Array.isArray(relationSummary) ? relationSummary : []).filter(Boolean),
    dependencySummary,
    givenSummary: (Array.isArray(givenSummary) ? givenSummary : []).filter(Boolean),
    warnings
  };
}

export function buildGeometryFigureResult(lines, stageSize, labelOverrides = {}, options = {}) {
  const classified = (Array.isArray(lines) ? lines : [])
    .map((line, index) => classifyStroke(line, index))
    .filter(Boolean);

  const segmentStrokes = classified.filter((item) => item.kind === "segment");
  const arcStrokes = classified.filter((item) => item.kind === "circular_arc");
  const circleStrokes = classified.filter((item) => item.kind === "circle");
  const rightAngleMarkStrokes = classified.filter((item) => item.kind === "right_angle_mark");
  const parallelArrowMarkStrokes = classified.filter((item) => item.kind === "parallel_arrow_mark");
  const angleArcMarkStrokes = classified.filter((item) => item.kind === "angle_arc_mark");
  const lengthTickMarkStrokes = classified.filter((item) => item.kind === "length_tick_mark");
  const markStrokes = classified.filter((item) => item.kind === "mark");
  const angleArcCandidates = [
    ...angleArcMarkStrokes,
    ...markStrokes.filter((mark) => Array.isArray(mark?.points) && mark.points.length >= 4 && (mark?.diagonal || 0) >= 12)
  ];
  const nodes = [];

  const rawSegments = segmentStrokes.map((stroke, index) => {
    const segmentId = `segment_${index + 1}`;
    const order = index + 1;
    const from = mergeNode(nodes, stroke.start, 18, { source: "stroke", splitOrder: order });
    const to = mergeNode(nodes, stroke.end, 18, { source: "stroke", splitOrder: order });
    const strokeStyle = normalizeSegmentStrokeStyle(
      options?.segmentStyleOverrides?.[segmentId],
      stroke.strokeStyle
    );
    return {
      id: segmentId,
      from,
      to,
      order,
      color: String(stroke.color || "#0f172a"),
      strokeStyle,
      dash: resolveSegmentDashPattern(strokeStyle),
      confidence: stroke.confidence,
      start: stroke.start,
      end: stroke.end
    };
  });

  const dependencies = [];
  for (let i = 0; i < rawSegments.length; i += 1) {
    for (let j = i + 1; j < rawSegments.length; j += 1) {
      const intersection = intersectSegments(rawSegments[i], rawSegments[j]);
      if (!intersection) continue;
      const sameAsEndpoint =
        distance(intersection, rawSegments[i].from) <= 10 ||
        distance(intersection, rawSegments[i].to) <= 10 ||
        distance(intersection, rawSegments[j].from) <= 10 ||
        distance(intersection, rawSegments[j].to) <= 10;
      if (sameAsEndpoint) continue;
      const node = mergeNode(nodes, intersection, 12, {
        source: "computed",
        splitOrder: Math.max(Number(rawSegments[i].order || 0), Number(rawSegments[j].order || 0))
      });
      dependencies.push({
        type: "intersection",
        pointId: node.id,
        refs: [rawSegments[i].id, rawSegments[j].id]
      });
    }
  }

  const arcs = arcStrokes
    .map((stroke, index) => {
      const arcId = `arc_${index + 1}`;
      const overrideCenter =
        options?.arcOverrides?.[arcId]?.center &&
        Number.isFinite(Number(options.arcOverrides[arcId].center.x)) &&
        Number.isFinite(Number(options.arcOverrides[arcId].center.y))
          ? {
              x: Number(options.arcOverrides[arcId].center.x),
              y: Number(options.arcOverrides[arcId].center.y)
            }
          : null;
      const geometry = resolveArcStrokeGeometry(stroke, overrideCenter);
      if (!geometry) return null;
      const startNode = findNearestNode(nodes, geometry.startPoint, 16);
      const endNode = findNearestNode(nodes, geometry.endPoint, 16);
      const centerNode = mergeNode(nodes, geometry.center, 14, {
        source: "computed",
        role: "circle_center"
      });
      const labelPosition = geometry.points[Math.max(1, Math.floor(geometry.points.length / 2))] || geometry.midpoint;
      return {
        id: arcId,
        ...geometry,
        centerNode,
        center: {
          x: centerNode.x,
          y: centerNode.y
        },
        confidence: stroke.confidence,
        startNodeId: startNode?.id || null,
        endNodeId: endNode?.id || null,
        labelPosition
      };
    })
    .filter(Boolean);

  const provisionalCircles = circleStrokes.map((stroke, index) => {
    const circleId = `circle_${index + 1}`;
    const override = options?.circleOverrides?.[circleId];
    const center =
      override?.center &&
      Number.isFinite(Number(override.center.x)) &&
      Number.isFinite(Number(override.center.y))
        ? {
            x: Number(override.center.x),
            y: Number(override.center.y)
          }
        : stroke.center;
    const radius =
      Number.isFinite(Number(override?.radius)) && Number(override.radius) > 0
        ? Number(override.radius)
        : stroke.radius;
    return {
      id: circleId,
      center,
      radius,
      confidence: stroke.confidence,
      label: String(options?.suggestedCircleLabels?.[index] || "").trim() || null
    };
  });
  const guidedCircles = (Array.isArray(options?.guidedCircles) ? options.guidedCircles : [])
    .map((circle, index) => {
      const circleId = String(circle?.id || `guided_circle_${index + 1}`).trim() || `guided_circle_${index + 1}`;
      const override = options?.circleOverrides?.[circleId];
      const center =
        override?.center &&
        Number.isFinite(Number(override.center.x)) &&
        Number.isFinite(Number(override.center.y))
          ? {
              x: Number(override.center.x),
              y: Number(override.center.y)
            }
          : {
              x: Number(circle?.center?.x || 0),
              y: Number(circle?.center?.y || 0)
            };
      const radius =
        Number.isFinite(Number(override?.radius)) && Number(override.radius) > 0
          ? Number(override.radius)
          : Number(circle?.radius || 0);
      if (!Number.isFinite(center.x) || !Number.isFinite(center.y) || !Number.isFinite(radius) || radius <= 0) {
        return null;
      }
      return {
        id: circleId,
        center,
        radius,
        confidence: 1,
        label: String(circle?.label || "").trim() || null,
        source: "guide"
      };
    })
    .filter(Boolean);
  guidedCircles.forEach((guidedCircle) => {
    if (provisionalCircles.some((circle) => circle.id === guidedCircle.id)) return;
    provisionalCircles.push(guidedCircle);
  });

  buildCurveIntersectionPoints(rawSegments, arcs, provisionalCircles).forEach((point) => {
    mergeNode(nodes, point.point, 12, {
      source: "computed",
      splitOrder: point.splitOrder
    });
  });

  const {
    segments,
    supportSegments,
    onSegmentRelations,
    collinearRelations
  } = buildSegmentTopology(rawSegments, nodes);

  const matchedRightAngleMarks = new Set();
  rightAngleMarkStrokes.forEach((mark) => {
    const match = matchRightAngleMarkToSegments(mark, supportSegments, nodes);
    if (!match) return;
    dependencies.push(match);
    matchedRightAngleMarks.add(mark.id);
  });
  const parallelArrowMatch = matchParallelArrowMarksToSegments(parallelArrowMarkStrokes, segments);
  const parallelArrowGroups = applyMarkerCountOverrides(
    buildParallelArrowGroups(parallelArrowMatch.attachments),
    options?.familyOverrides
  );
  const parallelDependencies = buildParallelDependencies(parallelArrowGroups);
  parallelDependencies.forEach((dependency) => dependencies.push(dependency));
  const matchedAngleArcMarks = new Set();
  const angleArcMatches = [];
  angleArcCandidates.forEach((mark) => {
    const match = matchAngleArcMarkToSegments(mark, segments);
    if (!match) return;
    angleArcMatches.push(match);
    matchedAngleArcMarks.add(mark.id);
  });
  const angleMarkDependencies = applyAngleMarkOverrides(
    buildGroupedAngleMarkDependencies(angleArcMatches),
    options?.familyOverrides
  );
  angleMarkDependencies.forEach((dependency) => dependencies.push(dependency));
  const equalAngleDependencies = buildEqualAngleDependencies(angleMarkDependencies);
  equalAngleDependencies.forEach((dependency) => dependencies.push(dependency));
  const lengthTickMatch = matchLengthTickMarksToSegments(lengthTickMarkStrokes, segments);
  const lengthTickGroups = applyMarkerCountOverrides(
    buildLengthTickGroups(lengthTickMatch.attachments),
    options?.familyOverrides
  );
  const equalLengthDependencies = buildEqualLengthDependencies(lengthTickGroups);
  equalLengthDependencies.forEach((dependency) => dependencies.push(dependency));
  const midpointDependencies = buildMidpointDependencies(equalLengthDependencies, segments, supportSegments);
  midpointDependencies.forEach((dependency) => dependencies.push(dependency));
  const medianDependencies = buildMedianDependencies(midpointDependencies, segments, supportSegments);
  medianDependencies.forEach((dependency) => dependencies.push(dependency));

  const circles = provisionalCircles.map((circle) => {
    const centerNode = mergeNode(nodes, circle.center, 14, {
      source: "computed",
      role: "circle_center"
    });
    return {
      ...circle,
      centerNode,
      center: {
        x: centerNode.x,
        y: centerNode.y
      }
    };
  });
  const pointLabelData = buildPointLabelData(nodes, labelOverrides, stageSize, options);
  const pointLabels = pointLabelData.labels;
  const hiddenPointLabels = options?.hiddenPointLabels || {};
  const pointDisplayLabels = nodes.reduce((acc, node) => {
    const semanticLabel = String(pointLabels[node.id] || "").trim();
    if (hiddenPointLabels[node.id]) {
      acc[node.id] = "";
      return acc;
    }
    acc[node.id] = unwrapTemplateToken(semanticLabel) || semanticLabel;
    return acc;
  }, {});
  const circularSemanticRelations = buildCircularSemanticRelations(
    nodes,
    arcs,
    circles,
    pointLabels,
    supportSegments
  );
  const segmentLabelMap = buildSegmentLabelMap([...supportSegments, ...segments], pointLabels);
  const warnings = [];
  const unresolvedMarks = [
    ...markStrokes.filter((mark) => (mark?.diagonal || 0) <= 64 && !matchedAngleArcMarks.has(mark.id)),
    ...parallelArrowMarkStrokes.filter((mark) => !parallelArrowMatch.matchedMarkIds.has(mark.id)),
    ...lengthTickMarkStrokes.filter((mark) => !lengthTickMatch.matchedMarkIds.has(mark.id)),
    ...rightAngleMarkStrokes.filter((mark) => !matchedRightAngleMarks.has(mark.id)),
    ...angleArcMarkStrokes.filter((mark) => !matchedAngleArcMarks.has(mark.id))
  ];
  if (unresolvedMarks.length) {
    warnings.push(`${unresolvedMarks.length} unresolved mark(s) remain. Add labels manually if needed.`);
  }
  if (!segments.length && !circles.length && !arcs.length) {
    warnings.push("No geometry primitives detected yet.");
  }

  collinearRelations
    .filter(
      (relation) =>
        relation.reason === "extension" && Array.isArray(relation.refs) && relation.refs.length >= 2
    )
    .forEach((relation, index) => {
      dependencies.push({
        id: `line_extension_${index + 1}`,
        type: "line_extension",
        pointId: relation.pointIds?.[1],
        pointIds: relation.pointIds,
        refs: relation.refs
      });
    });

  const finalizedDependencies = dedupeDependencies(dependencies);

  finalizedDependencies.forEach((dependency) => {
    const resolvedRefs = (Array.isArray(dependency.refs) ? dependency.refs : []).map(
      (ref) => segmentLabelMap[ref] || ref
    );
    if (dependency.type === "equal_length") {
      const tickSuffix =
        Number.isFinite(Number(dependency.tickCount)) && Number(dependency.tickCount) > 0
          ? ` (${dependency.tickCount} tick${Number(dependency.tickCount) === 1 ? "" : "s"})`
          : "";
      dependency.description = `${resolvedRefs.join(" and ")} are equal in length${tickSuffix}.`;
      return;
    }
    if (dependency.type === "parallel") {
      const arrowSuffix =
        Number.isFinite(Number(dependency.arrowCount)) && Number(dependency.arrowCount) > 0
          ? ` (${dependency.arrowCount} arrow${Number(dependency.arrowCount) === 1 ? "" : "s"})`
          : "";
      dependency.description = `${resolvedRefs.join(" and ")} are parallel${arrowSuffix}.`;
      return;
    }
    if (dependency.type === "midpoint") {
      dependency.description = `${pointLabels[dependency.pointId]} is the midpoint of ${resolvedRefs.join(" and ")}.`;
      return;
    }
    if (dependency.type === "median") {
      const [medianSegmentRef, baseSegmentRef] = resolvedRefs;
      dependency.description = `${medianSegmentRef} is a median to ${baseSegmentRef}.`;
      return;
    }
    if (dependency.type === "perpendicular") {
      dependency.description = `${resolvedRefs.join(" and ")} are perpendicular at ${pointLabels[dependency.pointId]}.`;
      return;
    }
    if (dependency.type === "line_extension") {
      const extensionPoints = (Array.isArray(dependency.pointIds) ? dependency.pointIds : [])
        .map((pointId) => pointLabels[pointId] || pointId)
        .filter(Boolean);
      if (extensionPoints.length >= 3) {
        dependency.description = `${extensionPoints[0]}${extensionPoints[1]} is extended through ${extensionPoints[1]} to ${extensionPoints[2]}.`;
      } else {
        dependency.description = `${resolvedRefs.join(" and ")} form a line extension.`;
      }
      return;
    }
    if (dependency.type === "angle_mark") {
      const angleLabel = dependency.anglePointIds
        .map((pointId) => pointLabels[pointId] || pointId)
        .join("");
      const arcSuffix =
        Number.isFinite(Number(dependency.arcCount)) && Number(dependency.arcCount) > 1
          ? ` (${dependency.arcCount} arcs)`
          : "";
      dependency.description = `Angle ${angleLabel} is marked at ${pointLabels[dependency.pointId]}${arcSuffix}.`;
      return;
    }
    if (dependency.type === "equal_angle") {
      const angleLabels = (Array.isArray(dependency.anglePointSets) ? dependency.anglePointSets : [])
        .map((anglePointIds) =>
          (Array.isArray(anglePointIds) ? anglePointIds : [])
            .map((pointId) => pointLabels[pointId] || pointId)
            .join("")
        )
        .filter(Boolean);
      const arcSuffix =
        Number.isFinite(Number(dependency.arcCount)) && Number(dependency.arcCount) > 0
          ? ` (${dependency.arcCount} arc${Number(dependency.arcCount) === 1 ? "" : "s"})`
          : "";
      dependency.description = `${angleLabels.join(" and ")} are equal in measure${arcSuffix}.`;
      return;
    }
    dependency.description = `${pointLabels[dependency.pointId]} depends on ${resolvedRefs.join(" and ")} via intersection.`;
  });

  const rawTriangleRelations = buildTriangleRelations(supportSegments);
  const triangleRelations = rawTriangleRelations.map((relation) => {
    const refs = relation.refs.map((pointId) => pointLabels[pointId] || pointId);
    return {
      ...relation,
      refs,
      description: `Triangle uses vertices ${refs.join(", ")}.`
    };
  });
  const supportSegmentMap = new Map(
    supportSegments.map((segment) => [segment.id, segment])
  );
  const topologyRelations = [
    ...onSegmentRelations
      .map((relation, index) => {
        const pointLabel = String(pointLabels[relation.pointId] || relation.pointId).trim();
        const refs = relation.refs
          .map((ref) => segmentLabelMap[ref] || ref)
          .filter(Boolean);
        const endpointLabels = relation.refs.flatMap((ref) => {
          const segment = supportSegmentMap.get(ref);
          if (!segment) return [];
          return [
            String(pointLabels[segment.from?.id] || segment.from?.id || "").trim(),
            String(pointLabels[segment.to?.id] || segment.to?.id || "").trim()
          ].filter(Boolean);
        });
        if (!pointLabel || !refs.length) return null;
        if (endpointLabels.includes(pointLabel)) return null;
        return {
          id: `on_segment_${index + 1}`,
          type: "on_segment",
          point: pointLabel,
          refs,
          description: `${pointLabel} lies on ${refs.join(" and ")}.`
        };
      })
      .filter(Boolean),
    ...collinearRelations.map((relation, index) => {
      const refs = relation.pointIds
        .map((pointId) => String(pointLabels[pointId] || pointId).trim())
        .filter(Boolean)
        .filter((pointLabel, pointIndex, list) => list.indexOf(pointLabel) === pointIndex);
      if (refs.length < 3) return null;
      return {
        id: `collinear_${index + 1}`,
        type: "collinear",
        refs,
        description: `${refs.join(", ")} are collinear.`
      };
    }).filter(Boolean)
  ];
  const relationSummary = topologyRelations
    .concat(circularSemanticRelations)
    .map((relation) => relation.description)
    .filter(Boolean)
    .filter((description, index, list) => list.indexOf(description) === index);
  const circleSuggestions = buildCircleSuggestions(supportSegments, rawTriangleRelations);
  const rightAngleMarkers = buildRightAngleMarkers(finalizedDependencies, [...supportSegments, ...segments], nodes);
  const angleEntities = buildAngleEntities(finalizedDependencies, segments, pointLabels);
  const angleCandidates = buildAngleCandidates(finalizedDependencies, segments, pointLabels);
  const logicalSegmentLabelTargets = buildLogicalSegmentLabelTargets(
    supportSegments,
    nodes,
    pointDisplayLabels,
    options?.segmentLabelOverrides
  );
  const segmentLabelAnnotations = buildSegmentLabelAnnotations(
    logicalSegmentLabelTargets,
    pointDisplayLabels,
    options?.segmentLabelOverrides
  );
  const angleLabelAnnotations = buildAngleLabelAnnotations(angleCandidates, options?.angleLabelOverrides);
  const arcLabelAnnotations = buildArcLabelAnnotations(arcs, pointLabels, options?.arcLabelOverrides);
  const labeledAngleEntities = angleLabelAnnotations
    .map((annotation) => ({
      id: annotation.description,
      label: annotation.description,
      type: "angle",
      points: annotation.refs.map((pointId) => pointLabels[pointId] || pointId)
    }))
    .filter((item) => item.id)
    .filter((item, index, list) => list.findIndex((other) => other.id === item.id) === index);
  const combinedAngleEntities = [...angleEntities];
  labeledAngleEntities.forEach((item) => {
    if (!combinedAngleEntities.some((existing) => existing.id === item.id)) {
      combinedAngleEntities.push(item);
    }
  });
  const angleArcMarkers = buildAngleArcMarkers(finalizedDependencies, segments, nodes);
  const lengthTickMarkers = buildLengthTickMarkers(lengthTickGroups, segments);
  const parallelArrowMarkers = buildParallelArrowMarkers(parallelArrowGroups, segments);
  const figureLabelAnnotations = [...segmentLabelAnnotations, ...angleLabelAnnotations, ...arcLabelAnnotations];
  const givenEntries = [
    ...segmentLabelAnnotations.map((annotation) => ({
      type: "segment_label",
      refs: annotation.refs,
      value: annotation.text,
      labelKind: annotation.kind,
      description: `${annotation.refs[0]} is labeled ${annotation.displayText || annotation.text}.`
    })),
    ...angleLabelAnnotations.map((annotation) => ({
      type: "angle_label",
      refs: [annotation.description],
      value: annotation.text,
      labelKind: annotation.kind,
      description: `Angle ${annotation.description} is labeled ${annotation.displayText || annotation.text}.`
    })),
    ...arcLabelAnnotations.map((annotation) => ({
      type: "arc_label",
      refs: annotation.refs,
      value: annotation.text,
      labelKind: annotation.kind,
      description: `${annotation.description} is labeled ${annotation.displayText || annotation.text}.`
    }))
  ];

  const figureSpec = {
    canvas: {
      viewBox: "0 0 100 100",
      responsive: true,
      normalizedStageWidth: Math.max(1, Number(stageSize?.width) || 100),
      normalizedStageHeight: Math.max(1, Number(stageSize?.height) || 100)
    },
    entities: {
      points: nodes.map((node) => ({
        id: node.id,
        type: "fixed",
        pos: normalizePosition(node, stageSize),
        label: pointLabels[node.id],
        displayLabel: pointDisplayLabels[node.id]
      })),
      lines: segments.map((segment) => ({
        id: segment.id,
        type: "segment",
        from: segment.from.id,
        to: segment.to.id,
        color: String(segment.color || "#0f172a"),
        strokeStyle: segment.strokeStyle || undefined,
        dash: Array.isArray(segment.dash) ? [...segment.dash] : undefined
      })),
      shapes: circles.map((circle) => ({
        id: circle.id,
        type: "circle",
        center: circle.centerNode.id,
        cx: normalizePosition(circle.center, stageSize)[0],
        cy: normalizePosition(circle.center, stageSize)[1],
        r: normalizeRadius(circle.radius, stageSize)
      }))
      .concat(
        arcs.map((arc) => ({
          id: arc.id,
          type: "arc",
          center: arc.centerNode?.id || null,
          centerPos: normalizePosition(arc.center, stageSize),
          r: normalizeRadius(arc.radius, stageSize),
          startAngle: Number(arc.startAngle.toFixed(6)),
          endAngle: Number((arc.startAngle + arc.sweepAngle).toFixed(6))
        }))
      )
      .concat(
        rightAngleMarkers.map((marker) => ({
          id: marker.id,
          type: "polyline",
          points: marker.points.map((point) => normalizePosition(point, stageSize))
        }))
      )
      .concat(
        angleArcMarkers.map((marker) => ({
          id: marker.id,
          type: "polyline",
          points: marker.points.map((point) => normalizePosition(point, stageSize))
        }))
      )
      .concat(
        lengthTickMarkers.map((marker) => ({
          id: marker.id,
          type: "polyline",
          points: marker.points.map((point) => normalizePosition(point, stageSize))
        }))
      )
      .concat(
        parallelArrowMarkers.map((marker) => ({
          id: marker.id,
          type: "polyline",
          points: marker.points.map((point) => normalizePosition(point, stageSize))
        }))
      ),
      annotations: figureLabelAnnotations.map((annotation, index) => ({
        id: annotation.id || `annotation_${index + 1}`,
        targetType: annotation.targetType,
        targetRef: annotation.targetRef,
        segment: annotation.segment,
        refs: annotation.refs,
        text: annotation.text,
        displayText: annotation.displayText,
        pos: normalizePosition(annotation.position, stageSize),
        anchorPos: annotation.anchorPosition ? normalizePosition(annotation.anchorPosition, stageSize) : null,
        radius: annotation.radius
      }))
    }
  };

  const diagramSchema = {
    entities: {
      points: nodes.map((node) => ({
        id: pointLabels[node.id],
        label: pointLabels[node.id],
        type: "point"
      })),
      lines: segments.map((segment) => ({
        id: `${pointLabels[segment.from.id]}${pointLabels[segment.to.id]}`,
        type: "segment",
        points: [pointLabels[segment.from.id], pointLabels[segment.to.id]]
      })),
      angles: combinedAngleEntities,
      arcs: arcs.map((arc, index) => ({
        id: arc.id,
        label: buildArcDisplayLabel(arc, pointLabels, index),
        type: "arc",
        points: [arc.startNodeId ? pointLabels[arc.startNodeId] : "", arc.endNodeId ? pointLabels[arc.endNodeId] : ""].filter(Boolean),
        center: arc.centerNode?.id ? pointLabels[arc.centerNode.id] || arc.centerNode.id : null,
        sweep: Number(arc.sweepDegrees.toFixed(2))
      })),
      circles: circles.map((circle) => ({
        id: circle.id,
        label: circle.label || circle.id,
        type: "circle",
        center: pointLabels[circle.centerNode.id] || circle.centerNode.id
      })),
      polygons: []
    },
    relations: [
      ...triangleRelations,
      ...topologyRelations,
      ...circularSemanticRelations,
      ...circles.map((circle) => ({
        type: "center_of_circle",
        point: pointLabels[circle.centerNode.id] || circle.centerNode.id,
        refs: [circle.id]
      })),
      ...finalizedDependencies.map((dependency) => ({
        type: dependency.type,
        point: pointLabels[dependency.pointId],
        refs:
          dependency.type === "equal_angle"
            ? (Array.isArray(dependency.anglePointSets) ? dependency.anglePointSets : [])
                .map((anglePointIds) =>
                  (Array.isArray(anglePointIds) ? anglePointIds : [])
                    .map((pointId) => pointLabels[pointId] || pointId)
                    .join("")
                )
                .filter(Boolean)
            : (Array.isArray(dependency.refs) ? dependency.refs : []).map((ref) => segmentLabelMap[ref] || ref),
        tickCount: Number.isFinite(Number(dependency.tickCount)) ? Number(dependency.tickCount) : undefined,
        arrowCount: Number.isFinite(Number(dependency.arrowCount)) ? Number(dependency.arrowCount) : undefined,
        arcCount: Number.isFinite(Number(dependency.arcCount)) ? Number(dependency.arcCount) : undefined,
        description: dependency.description
      }))
    ],
    givens: givenEntries,
    dependencies: finalizedDependencies.map((dependency) => ({
      type: dependency.type,
      point: pointLabels[dependency.pointId],
      refs:
        dependency.type === "equal_angle"
          ? (Array.isArray(dependency.anglePointSets) ? dependency.anglePointSets : [])
              .map((anglePointIds) =>
                (Array.isArray(anglePointIds) ? anglePointIds : [])
                  .map((pointId) => pointLabels[pointId] || pointId)
                  .join("")
              )
              .filter(Boolean)
          : (Array.isArray(dependency.refs) ? dependency.refs : []).map((ref) => segmentLabelMap[ref] || ref),
      tickCount: Number.isFinite(Number(dependency.tickCount)) ? Number(dependency.tickCount) : undefined,
      arrowCount: Number.isFinite(Number(dependency.arrowCount)) ? Number(dependency.arrowCount) : undefined,
      arcCount: Number.isFinite(Number(dependency.arcCount)) ? Number(dependency.arcCount) : undefined,
      description: dependency.description
    })),
    warnings
  };

  const summary = summarizeResult(
    pointLabels,
    segments,
    arcs,
    circles,
    combinedAngleEntities,
    finalizedDependencies,
    warnings,
    relationSummary,
    givenEntries.map((item) => item.description).filter(Boolean)
  );
  const confidencePool = [
    ...segments.map((segment) => Number(segment.confidence || 0)),
    ...arcs.map((arc) => Number(arc.confidence || 0)),
    ...circles.map((circle) => Number(circle.confidence || 0))
  ].filter((value) => Number.isFinite(value));
  const averageConfidence = confidencePool.length
    ? confidencePool.reduce((sum, value) => sum + value, 0) / confidencePool.length
    : 0.2;

  return {
    status: "pending",
    figureSpec,
    figureRenderSpec: figureSpec,
    diagramSchema,
    summary,
    warnings,
    confidence: {
      overall: Number(averageConfidence.toFixed(3))
    },
    primitives: {
      points: nodes.map((node) => ({
        id: node.id,
        x: Number(node.x.toFixed(2)),
        y: Number(node.y.toFixed(2)),
        label: pointLabels[node.id]
      })),
      segments: segments.map((segment) => ({
        id: segment.id,
        from: segment.from.id,
        to: segment.to.id,
        color: String(segment.color || "#0f172a"),
        strokeStyle: segment.strokeStyle || "solid",
        confidence: Number(segment.confidence.toFixed(3))
      })),
      arcs: arcs.map((arc, index) => ({
        id: arc.id,
        label: buildArcDisplayLabel(arc, pointLabels, index),
        cx: Number(arc.center.x.toFixed(2)),
        cy: Number(arc.center.y.toFixed(2)),
        radius: Number(arc.radius.toFixed(2)),
        centerNodeId: arc.centerNode?.id || null,
        startNodeId: arc.startNodeId,
        endNodeId: arc.endNodeId,
        sweepDegrees: Number(arc.sweepDegrees.toFixed(2)),
        confidence: Number(arc.confidence.toFixed(3))
      })),
      circles: circles.map((circle) => ({
        id: circle.id,
        cx: Number(circle.center.x.toFixed(2)),
        cy: Number(circle.center.y.toFixed(2)),
        radius: Number(circle.radius.toFixed(2)),
        confidence: Number(circle.confidence.toFixed(3))
      })),
      unresolvedMarks: unresolvedMarks.map((mark) => ({
        id: mark.id,
        bounds: mark.bounds
      })),
      lengthTickMarkers: lengthTickMarkers.map((marker) => ({
        id: marker.id,
        familyId: marker.familyId,
        segmentId: marker.segmentId,
        tickCount: marker.tickCount
      })),
      parallelArrowMarkers: parallelArrowMarkers.map((marker) => ({
        id: marker.id,
        familyId: marker.familyId,
        segmentId: marker.segmentId,
        arrowCount: marker.arrowCount
      })),
      rightAngleMarkers: rightAngleMarkers.map((marker) => ({
        id: marker.id,
        pointId: marker.pointId,
        refs: marker.refs
      })),
      angleArcMarkers: angleArcMarkers.map((marker) => ({
        id: marker.id,
        familyId: marker.familyId,
        pointId: marker.pointId,
        refs: marker.refs,
        arcCount: marker.arcCount
      })),
      labelAnnotations: figureLabelAnnotations.map((annotation) => ({
        id: annotation.id,
        targetType: annotation.targetType,
        targetRef: annotation.targetRef || null,
        refs: annotation.refs || [],
        text: annotation.text,
        displayText: annotation.displayText,
        kind: annotation.kind
      }))
    },
    preview: {
      points: nodes.map((node) => ({
        ...node,
        label: pointDisplayLabels[node.id],
        semanticLabel: pointLabels[node.id],
        hiddenLabel: Boolean(hiddenPointLabels[node.id])
      })),
      segments,
      arcs,
      circles,
      annotations: figureLabelAnnotations.map((annotation) => ({
        id: annotation.id,
        targetId: annotation.targetRef || annotation.id,
        x: annotation.position.x,
        y: annotation.position.y,
        anchorX: Number((annotation.anchorPosition?.x ?? annotation.position.x).toFixed(2)),
        anchorY: Number((annotation.anchorPosition?.y ?? annotation.position.y).toFixed(2)),
        text: annotation.displayText || annotation.text,
        kind: annotation.kind,
        targetType: annotation.targetType
      })),
      segmentLabelCandidates: buildSegmentLabelCandidates(
        supportSegments,
        nodes,
        pointDisplayLabels,
        options?.segmentLabelOverrides
      ),
      angleLabelCandidates: angleCandidates.map((candidate) => ({
        id: candidate.id,
        label: candidate.label,
        text: String(options?.angleLabelOverrides?.[candidate.id]?.text || "").trim(),
        kind: String(options?.angleLabelOverrides?.[candidate.id]?.kind || "constant").trim().toLowerCase() || "constant",
        x: Number(candidate.position.x.toFixed(2)),
        y: Number(candidate.position.y.toFixed(2))
      })),
      arcLabelCandidates: arcs.map((arc, index) => ({
        id: arc.id,
        label: buildArcDisplayLabel(arc, pointLabels, index),
        text: String(options?.arcLabelOverrides?.[arc.id]?.text || "").trim(),
        kind: String(options?.arcLabelOverrides?.[arc.id]?.kind || "constant").trim().toLowerCase() || "constant",
        x: Number((arc.labelPosition?.x || arc.center.x).toFixed(2)),
        y: Number((arc.labelPosition?.y || arc.center.y).toFixed(2))
      })),
      lengthTickMarkers,
      parallelArrowMarkers,
      rightAngleMarkers,
      angleArcMarkers,
      circleSuggestions,
      rawMarks: [
        ...markStrokes.filter((mark) => !matchedAngleArcMarks.has(mark.id)),
        ...parallelArrowMarkStrokes.filter((mark) => !parallelArrowMatch.matchedMarkIds.has(mark.id)),
        ...lengthTickMarkStrokes.filter((mark) => !lengthTickMatch.matchedMarkIds.has(mark.id)),
        ...rightAngleMarkStrokes.filter((mark) => !matchedRightAngleMarks.has(mark.id)),
        ...angleArcMarkStrokes.filter((mark) => !matchedAngleArcMarks.has(mark.id))
      ]
    }
  };
}
